<template>
  <div class="tasks-app flex flex-col h-full w-full bg-n-background">
    <header class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
      <div class="flex items-center gap-3 flex-wrap">
        <h1 class="text-xl font-extrabold text-n-slate-12">Tarefas</h1>
        <span v-if="tasks.length > 0" class="flex items-center gap-2 text-sm text-n-slate-10">
          <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" class="size-3.5 rounded cursor-pointer" :title="allSelected ? 'Desmarcar todas' : 'Selecionar todas'" />
          <span>{{ tasks.length }} total</span>
        </span>
        <span v-if="overdueStats > 0" class="text-xs px-2 py-0.5 rounded-md bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium">
          {{ overdueStats }} atrasada{{ overdueStats > 1 ? 's' : '' }}
        </span>
        <span v-if="todayStats > 0" class="text-xs px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-n-brand font-medium">
          {{ todayStats }} hoje
        </span>
        <span v-if="inProgressStats > 0" class="text-xs px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
          {{ inProgressStats }} em andamento
        </span>
      </div>
      <div class="flex items-center gap-3">
        <button class="px-3 py-1.5 bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 rounded-xl text-sm font-medium flex items-center gap-1.5" @click="showLabelsManager = true" title="Criar e gerenciar etiquetas">
          <span class="i-ri-price-tag-line size-3.5"/>
          Etiquetas
        </button>
        <button class="px-3 py-1.5 bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 rounded-xl text-sm font-medium flex items-center gap-1.5" @click="showTemplatesManager = true" title="Templates de tarefa">
          <span class="i-ri-file-text-line size-3.5"/>
          Templates
        </button>
        <button class="px-3 py-1.5 bg-n-brand hover:opacity-90 text-white rounded-xl text-sm font-medium" @click="openCreate">
          + Nova tarefa
        </button>
      </div>
    </header>

    <div class="flex flex-wrap items-center gap-4 px-6 py-3 border-b border-n-weak">
      <input v-model="filters.q" style="width: 240px;" type="text" placeholder="Buscar..." class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12 w-64" @input="debouncedFetch" />
      <select v-model="filters.status_key" style="width: 150px;" class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12" @change="fetchTasks">
        <option value="">Todos status</option>
        <option value="pending">Pendente</option>
        <option value="in_progress">Em andamento</option>
        <option value="done">Concluída</option>
        <option value="cancelled">Cancelada</option>
      </select>
      <select v-model="filters.priority" style="width: 170px;" class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12" @change="fetchTasks">
        <option value="">Toda prioridade</option>
        <option value="urgent">Urgente</option>
        <option value="high">Alta</option>
        <option value="medium">Média</option>
        <option value="low">Baixa</option>
        <option value="none">Sem prioridade</option>
      </select>
      <select v-model="filters.assigned_to_user_id" style="width: 180px;" class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12" @change="fetchTasks">
        <option value="">Todos atribuídos</option>
        <option v-for="u in agents" :key="u.id" :value="u.id">{{ u.name }}</option>
      </select>
      <select v-model="filters.sort" style="width: 220px;" class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12 ml-auto" @change="fetchTasks">
        <option value="due_at_asc">Vencimento (asc)</option>
        <option value="due_at_desc">Vencimento (desc)</option>
        <option value="created_desc">Mais recentes</option>
        <option value="priority">Prioridade</option>
      </select>
      <div class="ml-4 inline-flex items-center gap-2">
        <button :class="['px-4 py-2 text-xs rounded-xl border flex items-center gap-1.5 transition', viewMode === 'list' ? 'bg-n-brand text-white border-transparent' : 'bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 border-n-weak']" @click="viewMode = 'list'" title="Lista"><span class="i-ri-list-view size-3.5"/>Lista</button>
        <button :class="['px-4 py-2 text-xs rounded-xl border flex items-center gap-1.5 transition', viewMode === 'board' ? 'bg-n-brand text-white border-transparent' : 'bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 border-n-weak']" @click="viewMode = 'board'" title="Quadro"><span class="i-ri-layout-column-line size-3.5"/>Quadro</button>
        <button :class="['px-4 py-2 text-xs rounded-xl border flex items-center gap-1.5 transition', viewMode === 'agenda' ? 'bg-n-brand text-white border-transparent' : 'bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 border-n-weak']" @click="viewMode = 'agenda'" title="Agenda"><span class="i-ri-calendar-2-line size-3.5"/>Agenda</button>
      </div>
    </div>

    <div v-if="savedPresets.length > 0" class="flex flex-wrap items-center gap-1.5 px-6 py-2 border-b border-n-weak">
      <span class="text-xs text-n-slate-10 mr-1">Presets:</span>
      <button
        v-for="p in savedPresets"
        :key="p.id"
        type="button"
        :class="['text-xs px-2 py-0.5 rounded-md border transition', activePresetId === p.id ? 'bg-n-brand text-white border-transparent' : 'border-n-strong hover:border-n-brand text-n-slate-11']"
        @click="applyPreset(p)"
      >{{ p.name }}</button>
      <button v-if="activePresetId" type="button" class="text-xs text-n-slate-10 hover:text-red-500 ml-2" @click="clearPreset">limpar</button>
      <button type="button" class="text-xs text-n-brand hover:underline ml-auto" @click="savePreset">+ Salvar atual</button>
      <button v-if="activePresetId" type="button" class="text-xs text-red-500 hover:underline" @click="deletePreset">remover</button>
    </div>
    <div v-if="allLabels.length > 0" class="flex flex-wrap items-center gap-1.5 px-6 py-2 border-b border-n-weak">
      <span class="text-xs text-n-slate-10 mr-1">Etiquetas:</span>
      <button
        v-for="l in allLabels"
        :key="l.id"
        type="button"
        :class="['text-xs px-2 py-0.5 rounded-md border transition', filters.label_ids.includes(l.id) ? 'border-transparent ring-2 ring-offset-1 dark:ring-offset-slate-900' : 'border-n-strong hover:border-n-slate-9']"
        :style="filters.label_ids.includes(l.id) ? { backgroundColor: l.color + '33', color: l.color } : { color: l.color }"
        @click="toggleLabelFilter(l.id)"
      >{{ l.name }}</button>
      <button v-if="filters.label_ids.length > 0" type="button" class="text-xs text-n-slate-10 hover:text-red-500 ml-2" @click="filters.label_ids = []">limpar</button>
    </div>

    <main class="flex-1 overflow-y-auto p-6">
      <div v-if="selectedTaskIds.length > 0" class="mb-3 px-4 py-2 rounded-xl bg-n-alpha-1 border border-n-weak flex items-center gap-3 flex-wrap">
        <span class="text-sm font-semibold text-n-brand">{{ selectedTaskIds.length }} selecionada(s)</span>
        <button class="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white" @click="bulkComplete">Concluir</button>
        <button class="text-xs px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white" @click="bulkSetStatus('in_progress')">Em andamento</button>
        <button class="text-xs px-2 py-1 rounded bg-n-slate-12 hover:bg-n-slate-11 text-n-background" @click="bulkSetStatus('pending')">Pendente</button>
        <button class="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white" @click="bulkDelete">Excluir</button>
        <button class="text-xs px-2 py-1 rounded bg-n-solid-1 text-n-slate-11 ml-auto" @click="selectedTaskIds = []">Cancelar</button>
      </div>

      <div v-if="isFetching && filteredTasks.length === 0" class="text-center text-n-slate-10 py-10">Carregando...</div>
      <div v-else-if="filteredTasks.length === 0" class="text-center py-20">
        <div class="inline-flex size-16 rounded-full bg-n-alpha-1 items-center justify-center mb-4">
          <span class="i-ri-list-check size-8 text-n-brand dark:text-blue-400" />
        </div>
        <p class="text-lg font-extrabold text-n-slate-11 mb-1">Sem tarefas por aqui</p>
        <p class="text-sm text-n-slate-10 mb-4">Comece organizando seu trabalho</p>
        <button class="px-4 py-2 bg-n-brand hover:opacity-90 text-white rounded-xl text-sm font-medium" @click="openCreate">+ Criar primeira tarefa</button>
      </div>
      <ul v-else-if="viewMode === 'list'" class="space-y-2">
        <li v-for="t in filteredTasks" :key="t.id" :class="['group flex items-start gap-3 p-3 rounded-xl border border-n-weak hover:bg-n-alpha-1 cursor-pointer transition-colors relative']" :title="t.description || ''" @click="openEdit(t)">
          <input type="checkbox" :checked="selectedTaskIds.includes(t.id)" class="mt-1 size-4 rounded border-n-weak cursor-pointer" title="Selecionar" @click.stop="toggleSelectTask(t.id)" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span :class="['size-2 rounded-full inline-block', priorityColor(t.priority)]"></span>
              <span :class="titleClass(t)">{{ t.title }}</span>
              <span v-for="l in t.labels" :key="l.id" class="text-xs px-1.5 py-0.5 rounded" :style="labelStyle(l)">{{ l.name }}</span>
            </div>
            <p v-if="t.description" class="text-xs text-n-slate-10 mt-0.5 line-clamp-1">{{ t.description }}</p>
            <div class="flex items-center gap-3 mt-1 text-xs text-n-slate-10 flex-wrap">
              <span v-if="t.due_at" :class="t.overdue ? 'text-red-500 font-medium' : ''">{{ formatDate(t.due_at) }}</span>
              <span v-if="t.assigned_to" class="inline-flex items-center gap-1"><span class="ts-avatar-tiny" :style="avatarStyle(t.assigned_to)">{{ avatarInitial(t.assigned_to) }}</span><span>{{ t.assigned_to.name }}</span></span>
              <span v-if="t.recurrence_rule" title="Tarefa recorrente" class="i-ri-repeat-line size-3 text-n-slate-9"/>
              <span v-if="t.reminder_offsets && t.reminder_offsets.length > 0" :title="t.reminder_offsets.length + ' lembretes'" class="i-ri-bell-line size-3 text-n-slate-9"/>
              <span v-if="t.subtasks_count > 0">{{ t.subtasks_completed }}/{{ t.subtasks_count }} subtarefas</span>
              <span v-if="t.comments_count > 0" class="hover:text-n-brand hover:underline cursor-pointer" @click.stop="openEdit(t); pendingScrollToComments = true">{{ t.comments_count }} coment.</span>
              <span v-if="t.has_unread_mention" class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" title="Voce foi mencionado">@menção</span>
              <span v-if="t.public_token" class="i-ri-link size-3 text-emerald-500" title="Tarefa compartilhada via link publico"/>
            </div>
          </div>
          <div class="relative">
            <button type="button" class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap hover:ring-2 hover:ring-blue-400" :style="statusStyle(t)" @click.stop="statusChangerForTaskId = (statusChangerForTaskId === t.id ? null : t.id)">{{ statusLabel(t) }}</button>
            <div v-if="statusChangerForTaskId === t.id" v-on-click-outside="() => statusChangerForTaskId = null" class="absolute right-0 top-full mt-1 z-30 rounded-md shadow-lg border border-n-weak bg-n-solid-1 overflow-hidden min-w-[140px]">
              <button v-for="opt in statusOptions" :key="opt.key" type="button" class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-left hover:bg-n-alpha-2" @click.stop="quickSetStatus(t, opt.key)">
                <span class="size-2 rounded-full" :style="{backgroundColor: opt.color}"></span>
                <span class="text-n-slate-11">{{ opt.label }}</span>
              </button>
            </div>
          </div>
          <div class="ts-card-actions flex items-center gap-1 ml-1">
            <button type="button" class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-emerald-600" :title="isDone(t) ? 'Reabrir' : 'Concluir'" @click.stop="toggleComplete(t)">
              <span :class="isDone(t) ? 'i-ri-anticlockwise-line' : 'i-ri-check-line'" class="size-3.5" />
            </button>
            <button type="button" class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-n-brand" title="Duplicar tarefa" @click.stop="duplicateTask(t)">
              <span class="i-ri-file-copy-line size-3.5" />
            </button>
            <div class="relative">
              <button type="button" class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-amber-500" title="Adiar" @click.stop="snoozeForTaskId = (snoozeForTaskId === t.id ? null : t.id)">
                <span class="i-ri-time-line size-3.5" />
              </button>
              <div v-if="snoozeForTaskId === t.id" v-on-click-outside="() => snoozeForTaskId = null" class="absolute right-0 top-full mt-1 z-30 rounded-md shadow-lg border border-n-weak bg-n-solid-1 overflow-hidden min-w-[140px]">
                <button v-for="opt in snoozeOptions" :key="opt.key" type="button" class="block w-full px-3 py-1.5 text-xs text-left hover:bg-n-alpha-2 text-n-slate-11" @click.stop="snoozeTask(t, opt.days, opt.hour)">
                  {{ opt.label }}
                </button>
              </div>
            </div>
            <button type="button" class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-red-500" title="Excluir tarefa" @click.stop="quickDelete(t)">
              <span class="i-ri-delete-bin-line size-3.5" />
            </button>
          </div>
        </li>
      </ul>

      <div v-else-if="viewMode === 'board'" class="ts-board-wrap">
        <div v-for="col in boardColumns" :key="col.key" class="ts-board-col">
          <div class="ts-board-head" :style="{ borderTopColor: col.color }">
            <div class="flex items-center gap-2">
              <span class="size-2 rounded-full" :style="{ backgroundColor: col.color }"></span>
              <h3 class="text-sm font-semibold text-n-slate-11">{{ col.label }}</h3>
              <span class="text-xs text-n-slate-10">{{ tasksByStatus[col.key].length }}</span>
            </div>
          </div>
          <draggable
            :list="tasksByStatus[col.key]"
            group="tasks"
            item-key="id"
            class="ts-board-body"
            ghost-class="opacity-50"
            @change="onBoardChange($event, col.key)"
          >
            <template #item="{ element: t }">
              <div class="ts-board-card" @click="openEdit(t)">
                <div class="flex items-center gap-2 mb-1">
                  <span :class="['size-1.5 rounded-full inline-block shrink-0', priorityColor(t.priority)]"></span>
                  <span class="text-xs font-medium text-n-slate-12 truncate">{{ t.title }}</span>
                </div>
                <div v-if="t.due_at || t.assigned_to || t.recurrence_rule || (t.reminder_offsets && t.reminder_offsets.length)" class="flex items-center gap-2 text-[10px] text-n-slate-10 mt-1">
                  <span v-if="t.due_at" :class="t.overdue ? 'text-red-500 font-medium' : ''">{{ formatDate(t.due_at) }}</span>
                  <span v-if="t.assigned_to" class="truncate">{{ t.assigned_to.name }}</span>
                  <span v-if="t.recurrence_rule" title="Recorrente">🔁</span>
                  <span v-if="t.reminder_offsets && t.reminder_offsets.length > 0">🔔</span>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>

      <div v-else-if="viewMode === 'agenda'" class="space-y-4">
        <div v-for="day in agendaDays" :key="day.iso" class="rounded-xl border border-n-weak overflow-hidden">
          <div :class="['px-4 py-2 border-b border-n-weak flex items-center justify-between', day.isToday ? 'bg-n-alpha-1' : 'bg-n-alpha-1']">
            <div class="flex items-center gap-2">
              <span :class="['text-xs font-bold uppercase tracking-wide', day.isToday ? 'text-n-brand' : 'text-n-slate-10']">{{ day.dayLabel }}</span>
              <span class="text-sm font-semibold text-n-slate-12">{{ day.dateLabel }}</span>
              <span v-if="day.isToday" class="text-xs px-1.5 py-0.5 rounded bg-n-brand text-white">Hoje</span>
            </div>
            <span class="text-xs text-n-slate-10">{{ day.tasks.length }} {{ day.tasks.length === 1 ? 'tarefa' : 'tarefas' }}</span>
          </div>
          <div v-if="day.tasks.length === 0" class="px-4 py-3 text-xs text-n-slate-9">Sem tarefas</div>
          <draggable
            v-else
            :list="day.tasks"
            :group="{ name: 'agenda-tasks', pull: 'clone', put: true }"
            item-key="id"
            class="divide-y divide-n-weak"
            ghost-class="opacity-50"
            @change="onAgendaChange($event, day)"
          >
            <template #item="{ element: t }">
              <div class="px-4 py-2 flex items-center gap-3 hover:bg-n-alpha-1 cursor-pointer" @click="openEdit(t)">
              <input type="checkbox" :checked="isDone(t)" class="size-3.5 rounded cursor-pointer" @click.stop="toggleComplete(t)" />
              <span :class="['size-1.5 rounded-full inline-block shrink-0', priorityColor(t.priority)]"></span>
              <span class="text-xs text-n-slate-11 w-12 shrink-0 tabular-nums">{{ formatTime(t.due_at) }}</span>
              <span :class="['text-sm font-medium flex-1 truncate', isDone(t) ? 'line-through text-n-slate-9' : 'text-n-slate-12']">{{ t.title }}</span>
              <span v-if="t.assigned_to" class="text-xs text-n-slate-10 truncate hidden sm:inline">{{ t.assigned_to.name }}</span>
                <span class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap" :style="statusStyle(t)">{{ statusLabel(t) }}</span>
              </div>
            </template>
          </draggable>
        </div>
        <div v-if="agendaTasksWithoutDate.length > 0" class="rounded-xl border border-dashed border-n-strong px-4 py-3">
          <div class="text-xs font-semibold uppercase text-n-slate-10 mb-2">Sem data</div>
          <ul class="space-y-1">
            <li v-for="t in agendaTasksWithoutDate" :key="t.id" class="text-sm text-n-slate-11 cursor-pointer hover:underline" @click="openEdit(t)">
              {{ t.title }}
            </li>
          </ul>
        </div>
      </div>
    </main>

    <woot-modal v-if="modalOpen" :show="modalOpen" :on-close="closeModal" :show-close-button="false" size="medium">
      <div class="ts-modal" @dragover.prevent="onModalDragover" @dragleave.prevent="onModalDragleave" @drop.prevent="onModalDrop">
        <div v-if="dragOver" class="ts-modal-dropzone-overlay">
          <div class="ts-modal-dropzone-inner">
            <span class="i-ri-upload-cloud-line size-16 text-n-brand mb-3"/>
            <p class="text-lg font-extrabold text-n-slate-11">Solte o arquivo para anexar</p>
          </div>
        </div>

        <header class="ts-modal-header">
          <div class="ts-modal-header-row">
            <span class="ts-modal-priority-bullet" :style="{ backgroundColor: priorityHex(form.priority) }" :title="priorityLabel(form.priority)"></span>
            <input v-model="form.title" type="text" required placeholder="Nome da tarefa" class="ts-modal-title-input"/>
            <select v-model="form.status_key" class="ts-modal-status-pill" :style="statusPillStyleForKey(form.status_key)">
              <option v-for="opt in statusOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            </select>
            <button type="button" class="ts-icon-btn" title="Fechar" @click="closeModal">
              <span class="i-ri-close-line size-4"/>
            </button>
          </div>
          <div v-if="editingTask" class="ts-modal-header-meta">
            <span v-if="editingTask.created_by" class="ts-modal-meta-item">
              <span class="i-ri-user-line size-3"/>
              Criada por <strong>{{ editingTask.created_by.name }}</strong>
            </span>
            <span class="ts-modal-meta-item">
              <span class="i-ri-time-line size-3"/>
              {{ formatRelativeTime(editingTask.created_at) }}
            </span>
            <div class="ts-modal-actions ml-auto">
              <button type="button" class="ts-icon-btn" :class="editingShareUrl ? 'is-active-emerald' : ''" @click="openShareDialog" :title="editingShareUrl ? 'Link publico ativo' : 'Compartilhar via link'">
                <span class="i-ri-share-2-line size-4"/>
              </button>
              <button v-if="editingShareUrl" type="button" class="ts-icon-btn" @click="copyShareLink" title="Copiar link publico">
                <span class="i-ri-link size-4"/>
              </button>
              <button type="button" class="ts-icon-btn ts-icon-btn-danger" @click="deleteTask" title="Excluir tarefa">
                <span class="i-ri-delete-bin-line size-4"/>
              </button>
            </div>
          </div>
          <div v-if="!editingTask && availableTemplates.length > 0" class="ts-modal-template-bar">
            <span class="i-ri-file-text-line size-3.5 text-n-slate-9"/>
            <span class="text-xs text-n-slate-10">Aplicar template:</span>
            <select v-model="selectedTemplateId" class="ts-modal-template-select" @change="applyTemplateInline">
              <option :value="null">- nenhum -</option>
              <option v-for="t in availableTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
        </header>

        <nav class="ts-modal-tabs">
          <button v-for="tab in modalTabs" :key="tab.key" type="button" :class="['ts-modal-tab', activeTab === tab.key && 'is-active']" @click="activeTab = tab.key">
            <span :class="tab.icon + ' size-4'"/>
            <span>{{ tab.label }}</span>
            <span v-if="tab.count !== undefined && tab.count > 0" class="ts-modal-tab-count">{{ tab.count }}</span>
          </button>
        </nav>
      <form class="ts-modal-body" @submit.prevent="saveTask">
          <div v-show="activeTab === 'details'" class="ts-modal-pane">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Título *</label>
          <input v-model="form.title" type="text" required class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12" placeholder="O que precisa ser feito?" />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Descrição</label>
          <div class="relative">
            <textarea ref="descTextarea" v-model="form.description" rows="3" class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12" placeholder="Detalhes opcionais (use # pra etiquetas)" @input="onDescriptionInput" @keydown="onDescriptionKeydown"></textarea>
            <div v-if="hashtagOpen && filteredHashtagLabels.length > 0" class="absolute z-20 left-0 right-0 top-full mt-1 max-h-44 overflow-y-auto bg-n-solid-1 border border-n-weak rounded-md shadow-lg">
              <button
                v-for="(l, idx) in filteredHashtagLabels"
                :key="l.id"
                type="button"
                :class="['flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left', idx === hashtagActiveIdx ? 'bg-n-alpha-1' : '']"
                @click="selectHashtag(l)"
              >
                <span class="size-2 rounded-full" :style="{backgroundColor: l.color}"></span>
                <span class="text-n-slate-11">#{{ l.name }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">Vencimento</label>
            <date-picker
              v-model:value="form.due_at"
              type="datetime"
              format="DD/MM/YYYY HH:mm"
              value-type="format"
              :lang="ptBR"
              :clearable="true"
              :show-second="false"
              :minute-step="5"
              placeholder="Selecione data e hora" :append-to-body="true"
              class="w-full task-datepicker"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">Prioridade</label>
            <select v-model="form.priority" class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12">
              <option value="none">Sem prioridade</option>
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">Status</label>
            <select v-model="form.status_key" class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12">
              <option value="pending">Pendente</option>
              <option value="in_progress">Em andamento</option>
              <option value="done">Concluída</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">Atribuido a</label>
            <select v-model="form.assigned_to_user_id" class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12">
              <option :value="null">-- nenhum --</option>
              <option v-for="u in agents" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
        </div>

        <div class="ts-form-row relative" v-on-click-outside="() => contactSearchOpen = false">
          <label class="ts-form-label">Vincular a contato / conversa</label>
          <div v-if="form.contact_id || form.conversation_display_id" class="flex items-center gap-2 p-2 bg-n-alpha-1 rounded-md border border-n-weak">
            <span class="i-ri-user-line size-4 text-n-slate-11"/>
            <span class="flex-1 text-sm text-n-slate-12">{{ form.contact_name || ('Contato #' + form.contact_id) }}<span v-if="form.conversation_display_id" class="text-n-slate-10"> - conversa #{{ form.conversation_display_id }}</span></span>
            <a v-if="form.conversation_display_id" :href="conversationLink(form.conversation_display_id)" target="_blank" rel="noopener noreferrer" class="text-xs text-n-brand hover:underline">abrir</a>
            <button type="button" class="text-xs text-n-slate-10 hover:text-red-500" @click="clearContactLink">Desvincular</button>
          </div>
          <div v-else>
            <input v-model="contactSearchQuery" type="text" placeholder="Buscar por nome, telefone, email..." class="ts-form-input w-full" @input="onContactSearchInput" @focus="contactSearchOpen = true"/>
            <ul v-if="contactSearchOpen && contactSearchResults.length > 0" class="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-n-background border border-n-weak rounded-md shadow-lg z-30">
              <li v-for="c in contactSearchResults" :key="c.id" class="px-3 py-2 hover:bg-n-alpha-1 cursor-pointer" @click="pickContact(c)">
                <div class="flex items-center gap-2">
                  <span class="ts-avatar-sm" :style="avatarStyle(c)">{{ avatarInitial(c) }}</span>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-n-slate-12 truncate">{{ c.name || 'Sem nome' }}</div>
                    <div class="text-xs text-n-slate-10 truncate">{{ c.phone_number || c.email || ('#' + c.id) }}</div>
                  </div>
                </div>
              </li>
            </ul>
            <p v-if="contactSearchOpen && contactSearchQuery && contactSearchResults.length === 0 && !contactSearchLoading" class="text-xs text-n-slate-10 mt-1">Nenhum contato encontrado.</p>
            <p v-if="contactSearchLoading" class="text-xs text-n-slate-10 mt-1">Buscando...</p>
          </div>
        </div>

        <div class="space-y-2 pt-2 border-t border-n-weak">
          <div class="flex items-center gap-2">
            <input id="rec-toggle" v-model="recurrenceEnabled" type="checkbox" class="size-3.5 rounded cursor-pointer" />
            <label for="rec-toggle" class="text-sm font-medium text-n-slate-11 cursor-pointer">Repetir apos concluir</label>
          </div>
          <div v-if="recurrenceEnabled" class="grid grid-cols-2 gap-3 pl-5">
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">Frequencia</label>
              <select v-model="recurrenceFrequency" class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12">
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
                <option value="weekday">Dias uteis</option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">A cada</label>
              <input v-model.number="recurrenceInterval" type="number" min="1" class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12" />
            </div>
          </div>
        </div>

        <div class="space-y-2 pt-2 border-t border-n-weak">
          <label class="text-sm font-medium text-n-slate-11">Lembretes (antes do vencimento)</label>
          <div class="flex flex-wrap gap-2">
            <label v-for="opt in reminderOptions" :key="opt.value" class="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" :checked="form.reminder_offsets.includes(opt.value)" class="size-3.5 rounded cursor-pointer" @change="toggleReminderOffset(opt.value)" />
              <span class="text-xs text-n-slate-11">{{ opt.label }}</span>
            </label>
          </div>
        </div>
          </div><!-- end pane details -->

      <div class="ts-modal-extra">
        <div v-if="!editingTask && activeTab !== 'details'" class="ts-modal-pane">
          <div class="ts-empty-state">
            <span class="i-ri-information-line size-8 text-n-slate-10 mb-2"/>
            <p class="text-sm font-medium">Salve a tarefa primeiro</p>
            <p class="text-xs text-n-slate-10 mt-1">Subtarefas, anexos, tempo, dependências e comentários estarão disponíveis após salvar.</p>
          </div>
        </div>
        <template v-if="editingTask">
        <div v-show="activeTab === 'details'" class="ts-modal-pane">
          <div class="flex items-center justify-between mb-2">
            <h4 class="ts-section-title">Etiquetas</h4>
            <button type="button" class="text-xs text-n-brand hover:underline" @click="showLabelsManager = true">+ Gerenciar</button>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="l in allLabels"
              :key="l.id"
              type="button"
              :class="['text-xs px-2 py-1 rounded-md border transition', currentTaskLabels.includes(l.id) ? 'border-transparent ring-2 ring-offset-1' : 'border-n-strong hover:border-n-slate-9']"
              :style="currentTaskLabels.includes(l.id) ? { backgroundColor: l.color + '33', color: l.color, ringColor: l.color } : { color: l.color }"
              @click="toggleTaskLabel(l.id)"
            >{{ l.name }}</button>
            <span v-if="allLabels.length === 0" class="text-xs text-n-slate-9">Sem etiquetas cadastradas</span>
          </div>
        </div>

        <div v-show="activeTab === 'subtasks'" class="ts-modal-pane">
          <h4 class="ts-section-title">Subtarefas <span class="text-n-slate-9 font-normal">({{ currentSubtasks.filter(s => s.completed).length }}/{{ currentSubtasks.length }})</span></h4>
          <draggable
            v-if="currentSubtasks.length > 0"
            :list="currentSubtasks"
            item-key="id"
            class="space-y-1 mb-2"
            ghost-class="opacity-50"
            @end="onSubtaskReorder"
          >
            <template #item="{ element: s }">
              <div class="flex items-center gap-2 text-sm cursor-move">
              <input type="checkbox" :checked="s.completed" class="size-3.5 rounded cursor-pointer" @change="toggleSubtask(s)" />
              <span :class="['flex-1', s.completed ? 'line-through text-n-slate-9' : 'text-n-slate-11']">{{ s.title }}</span>
                <button type="button" class="text-n-slate-9 hover:text-red-500 text-xs" @click="removeSubtask(s)">×</button>
              </div>
            </template>
          </draggable>
          <div class="flex gap-2">
            <input
              v-model="newSubtaskTitle"
              type="text"
              placeholder="Adicionar subtarefa e Enter"
              class="flex-1 px-3 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
              @keyup.enter="addSubtask"
            />
            <button type="button" class="px-3 py-1.5 text-xs rounded-md bg-n-brand hover:opacity-90 text-white disabled:opacity-50" :disabled="savingSubtask" @click="addSubtask">+</button>
          </div>
        </div>

        
        <!-- v219: Anexos -->
        <div v-show="activeTab === 'attachments'" class="ts-modal-pane">
          <div class="flex items-center justify-between w-full">
            <h4 class="ts-section-title" style="margin: 0;">Anexos <span class="text-n-slate-9 font-normal">({{ attachments.length }})</span></h4>
            <label class="px-3 py-1.5 text-xs rounded-md bg-n-brand text-white hover:opacity-90 cursor-pointer flex items-center gap-1.5">
              <span class="i-ri-attachment-line size-3.5"/>
              Anexar arquivo
              <input ref="fileInput" type="file" class="hidden" @change="onAttachmentSelected" />
            </label>
          </div>
          <ul v-if="attachments.length > 0" class="flex flex-col gap-2 w-full">
            <li v-for="a in attachments" :key="a.id" class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition">
              <span class="i-ri-file-line size-5 text-n-slate-10 shrink-0"/>
              <a :href="a.url" target="_blank" rel="noopener noreferrer" class="flex-1 truncate text-sm text-n-brand hover:underline">{{ a.name }}</a>
              <span class="text-xs text-n-slate-9 shrink-0">{{ formatBytes(a.byte_size) }}</span>
              <button type="button" class="text-n-slate-9 hover:text-red-500 size-6 inline-flex items-center justify-center rounded" @click="removeAttachment(a)">x</button>
            </li>
          </ul>
          <div v-else class="flex flex-col items-center justify-center w-full py-12 px-6 border-2 border-dashed border-n-weak rounded-xl">
            <span class="i-ri-upload-cloud-line size-10 text-n-slate-9 mb-2"/>
            <p class="text-sm text-n-slate-10 mb-1">Nenhum anexo ainda</p>
            <p class="text-xs text-n-slate-9">Use o botao acima ou arraste um arquivo aqui</p>
          </div>
          <p v-if="attachmentUploading" class="text-xs text-n-slate-10 w-full text-left">Enviando...</p>
        </div>

        <div v-show="activeTab === 'time'" class="ts-modal-pane">
        <div class="flex items-center gap-3 p-2 rounded-md bg-n-alpha-1">
          <div class="flex-1 text-xs">
            <span class="text-n-slate-10">Tempo registrado:</span>
            <span class="font-mono ml-1 text-n-slate-11">{{ formatHMS(totalTrackedSeconds + (timerActive ? liveTickSeconds : 0)) }}</span>
            <span v-if="timerActive" class="text-emerald-500 ml-2">ao vivo</span>
            <button v-if="timeEntries.length > 0" type="button" class="ml-2 text-n-brand hover:underline" @click="showTimeHistory = !showTimeHistory">{{ showTimeHistory ? 'ocultar' : 'historico' }} ({{ timeEntries.length }})</button>
          </div>
          <button v-if="!timerActive" type="button" class="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium" @click="startTimer">Iniciar timer</button>
          <button v-else type="button" class="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium" @click="stopTimer">Parar</button>
        </div>
        <ul v-if="showTimeHistory && timeEntries.length > 0" class="space-y-1 pl-3 border-l-2 border-n-weak">
          <li v-for="e in timeEntries" :key="e.id" class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="font-mono w-16">{{ formatHMS(e.duration_seconds || 0) }}</span>
            <span class="text-n-slate-9">{{ formatRelativeTime(e.started_at) }}</span>
            <span v-if="e.active" class="text-emerald-500">em andamento</span>
            <button v-if="!e.active" type="button" class="ml-auto text-n-slate-10 hover:text-red-500" @click="removeTimeEntry(e)" title="Remover">x</button>
          </li>
        </ul>

        </div><!-- close time pane -->
        <div v-show="activeTab === 'deps'" class="ts-modal-pane">
          <h4 class="ts-section-title">Dependencias</h4>
          <div v-if="blockingTasks.length > 0" class="flex flex-col gap-2 w-full">
            <div class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">Bloqueada por:</div>
            <ul class="flex flex-col gap-2 w-full">
              <li v-for="bt in blockingTasks" :key="bt.id" class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-alpha-1">
                <span :class="['size-2 rounded-full shrink-0', bt.status_key === 'done' ? 'bg-emerald-500' : 'bg-amber-500']"></span>
                <span class="flex-1 text-sm text-n-slate-12 truncate">{{ bt.title }}</span>
                <span class="text-xs px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11">{{ bt.status_key }}</span>
                <button type="button" class="text-n-slate-9 hover:text-red-500 size-6 inline-flex items-center justify-center rounded" @click="removeDependency(bt.dep_id || bt.id)" title="Remover">x</button>
              </li>
            </ul>
          </div>
          <div class="flex flex-col gap-2 w-full relative">
            <label class="text-xs font-medium text-n-slate-11">Adicionar dependencia</label>
            <div class="relative w-full">
              <span class="absolute left-3 top-1/2 -translate-y-1/2 i-ri-search-line size-4 text-n-slate-10"/>
              <input v-model="newBlockerSearch" type="text" placeholder="Digite o nome da tarefa..." class="w-full pl-10 pr-3 py-2 rounded-xl border border-n-weak bg-n-alpha-1 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none" />
            </div>
            <ul v-if="newBlockerSearch && filteredBlockerCandidates.length > 0" class="absolute z-10 left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto border border-n-weak rounded-xl bg-n-background shadow-lg">
              <li v-for="t in filteredBlockerCandidates" :key="t.id" class="flex items-center gap-3 px-3 py-2.5 hover:bg-n-alpha-1 cursor-pointer text-sm" @click="addBlockingDep(t); newBlockerSearch = '';">
                <span :class="['size-2 rounded-full shrink-0', t.status_key === 'done' ? 'bg-emerald-500' : 'bg-amber-500']"></span>
                <span class="flex-1 text-n-slate-12 truncate">{{ t.title }}</span>
                <span class="text-xs text-n-slate-9 shrink-0">{{ t.status_key }}</span>
              </li>
            </ul>
            <div v-else-if="newBlockerSearch && filteredBlockerCandidates.length === 0" class="absolute z-10 left-0 right-0 top-full mt-1 px-3 py-3 border border-n-weak rounded-xl bg-n-background text-sm text-n-slate-10">Nenhuma tarefa encontrada</div>
          </div>
          <div v-if="blockingTasks.length === 0 && !newBlockerSearch" class="flex flex-col items-center justify-center w-full py-8 px-6 border-2 border-dashed border-n-weak rounded-xl">
            <span class="i-ri-link size-8 text-n-slate-9 mb-2"/>
            <p class="text-sm text-n-slate-10">Nenhuma dependencia</p>
            <p class="text-xs text-n-slate-9 mt-1">Use o campo acima para vincular outra tarefa</p>
          </div>
        </div>

        <div v-show="activeTab === 'activity'" class="ts-modal-pane">
        <div v-if="currentActivities.length > 0">
          <h4 class="ts-section-title">Histórico ({{ currentActivities.length }})</h4>
          <ul class="space-y-1.5">
            <li v-for="a in currentActivities.slice(0, 10)" :key="a.id" class="flex items-center gap-2 text-xs text-n-slate-10">
              <span class="ts-avatar-tiny" :style="avatarStyle(a.user)">{{ avatarInitial(a.user) }}</span>
              <span class="text-n-slate-11 font-medium shrink-0">{{ a.user?.name || 'sistema' }}</span>
              <span>{{ activityLabel(a.action) }}</span>
              <span class="ml-auto text-n-slate-9 shrink-0">{{ formatRelativeTime(a.created_at) }}</span>
            </li>
          </ul>
        </div>
        <div>
          <h4 data-comments-anchor class="text-xs font-bold uppercase text-n-slate-10 mb-2">Comentários <span class="text-n-slate-9 font-normal">({{ currentComments.length }})</span></h4>
          <ul v-if="currentComments.length > 0" class="space-y-2 mb-2">
            <li v-for="c in currentComments" :key="c.id" class="flex gap-2 p-2 rounded-md bg-n-alpha-1 group">
              <span class="ts-avatar-sm" :style="avatarStyle(c.author)">{{ avatarInitial(c.author) }}</span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 text-xs">
                  <span class="font-semibold text-n-slate-11">{{ c.author?.name || 'Sem autor' }}</span>
                  <span class="text-n-slate-9">{{ formatRelativeTime(c.created_at) }}</span>
                  <div class="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button v-if="editingCommentId !== c.id" type="button" class="text-n-slate-9 hover:text-n-brand text-xs" @click="startEditComment(c)" title="Editar">✎</button>
                    <button type="button" class="text-n-slate-9 hover:text-red-500" @click="removeComment(c)" title="Remover">×</button>
                  </div>
                </div>
                <div v-if="editingCommentId === c.id" class="mt-1 flex gap-2">
                  <textarea v-model="editingCommentContent" rows="2" class="flex-1 px-2 py-1 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"></textarea>
                  <div class="flex flex-col gap-1">
                    <button type="button" class="px-2 py-0.5 text-xs rounded bg-n-brand text-white" @click="saveCommentEdit">OK</button>
                    <button type="button" class="px-2 py-0.5 text-xs rounded bg-n-alpha-3 text-n-slate-11" @click="editingCommentId = null">×</button>
                  </div>
                </div>
                <div v-else class="text-sm text-n-slate-11 whitespace-pre-wrap mt-0.5" v-html="renderCommentContent(c.content)"></div>
              </div>
            </li>
          </ul>
          <div class="flex gap-2 relative">
            <div class="flex-1 relative">
              <textarea
                ref="commentTextarea"
                v-model="newCommentContent"
                rows="2"
                placeholder="Adicione um comentário... use @ para mencionar"
                class="w-full px-3 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
                @input="onCommentInput"
                @keydown="onCommentKeydown"
              ></textarea>
              <div v-if="mentionOpen && filteredMentionAgents.length > 0" class="absolute z-20 left-0 right-0 top-full mt-1 max-h-44 overflow-y-auto bg-n-solid-1 border border-n-weak rounded-md shadow-lg">
                <button
                  v-for="(a, idx) in filteredMentionAgents"
                  :key="a.id"
                  type="button"
                  :class="['flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-blue-50 dark:hover:bg-blue-900/20', idx === mentionActiveIdx ? 'bg-n-alpha-1' : '']"
                  @click="selectMention(a)"
                >
                  <span class="ts-avatar-tiny" :style="avatarStyle(a)">{{ avatarInitial(a) }}</span>
                  <span class="text-n-slate-11">{{ a.name }}</span>
                </button>
              </div>
            </div>
            <button type="button" class="ts-btn-primary-sm self-end" :disabled="savingComment" @click="addComment"><span class="i-ri-send-plane-line size-3.5"/>Enviar</button>
          </div>
        </div>
        </div><!-- close activity pane -->
        </template>
      </div><!-- close ts-modal-extra -->
      </form>

      <footer class="ts-modal-footer">
        <button type="button" class="ts-btn-ghost" @click="closeModal">Cancelar</button>
        <button type="submit" :disabled="saving" class="ts-btn-primary" @click="saveTask">
          <span v-if="saving" class="i-ri-loader-line size-4 animate-spin"/>
          <span v-else class="i-ri-check-line size-4"/>
          {{ saving ? 'Salvando...' : 'Salvar tarefa' }}
        </button>
      </footer>
      </div><!-- end ts-modal -->
    </woot-modal>

    <TaskLabelsManager v-if="showLabelsManager" @close="showLabelsManager = false" @changed="loadLabels" />
    <TaskTemplatesManager v-if="showTemplatesManager" @close="showTemplatesManager = false" @applied="onTemplateApplied" />
    <TaskShareDialog v-if="showShareDialog && editingTask" :task-id="editingTask.id" :initial-url="editingShareUrl" @close="showShareDialog = false" @changed="onShareChanged" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TaskSubtasksAPI from 'dashboard/api/taskSubtasks';
import TaskCommentsAPI from 'dashboard/api/taskComments';
import TaskLabelsAPI from 'dashboard/api/taskLabels';
import TaskLabelsManager from './TaskLabelsManager.vue';
import TaskTemplatesManager from './TaskTemplatesManager.vue';
import TaskShareDialog from './TaskShareDialog.vue';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import { vOnClickOutside } from '@vueuse/components';
import Draggable from 'vuedraggable';

export default {
  name: 'TasksApp',
  components: { DatePicker, draggable: Draggable, TaskLabelsManager, TaskTemplatesManager, TaskShareDialog },
  directives: { 'on-click-outside': vOnClickOutside },
  data() {
    return {
      modalOpen: false,
      editingTask: null,
      saving: false,
      ptBR: {
        formatLocale: {
          months: ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'],
          monthsShort: ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'],
          weekdays: ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'],
          weekdaysShort: ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'],
          weekdaysMin: ['D','S','T','Q','Q','S','S'],
          firstDayOfWeek: 0,
          firstWeekContainsDate: 1,
        },
        yearFormat: 'YYYY',
        monthBeforeYear: false,
      },
      debounceTimer: null,
      viewMode: 'list',
      currentSubtasks: [],
      currentComments: [],
      currentTaskLabels: [],
      currentActivities: [],
      allLabels: [],
      newSubtaskTitle: '',
      newCommentContent: '',
      savingSubtask: false,
      savingComment: false,
      showLabelsManager: false,
      showTemplatesManager: false,
      showShareDialog: false,
      editingShareUrl: '',
      activeTimer: null,
      timerActive: false,
      totalTrackedSeconds: 0,
      liveTickSeconds: 0,
      liveTickHandle: null,
      blockingTasks: [],
      blockedTasks: [],
      newBlockerSearch: '',
      availableTemplates: [],
      selectedTemplateId: null,
      pendingSubtasksFromTemplate: [],
      timeEntries: [],
      showTimeHistory: false,
      attachments: [],
      attachmentUploading: false,
      contactSearchQuery: '',
      contactSearchResults: [],
      contactSearchOpen: false,
      contactSearchLoading: false,
      contactSearchTimer: null,
      activeTab: 'details',
      dragOver: false,
      filtersOpen: false,
      mentionOpen: false,
      mentionFilter: '',
      mentionStartIdx: -1,
      mentionActiveIdx: 0,
      hashtagOpen: false,
      hashtagFilter: '',
      hashtagStartIdx: -1,
      hashtagActiveIdx: 0,
      recurrenceEnabled: false,
      recurrenceFrequency: 'daily',
      recurrenceInterval: 1,
      editingCommentId: null,
      editingCommentContent: '',
      selectedTaskIds: [],
      statusChangerForTaskId: null,
      savedPresets: [],
      activePresetId: null,
      pendingScrollToComments: false,
      snoozeForTaskId: null,
      filters: { q: '', status_key: '', priority: '', assigned_to_user_id: '', sort: 'due_at_asc', label_ids: [], blocked_only: false },
      form: { title: '', description: '', due_at: '', priority: 'medium', status_key: 'pending', assigned_to_user_id: null, recurrence_rule: null, reminder_offsets: [] },
    };
  },
  computed: {
    ...mapGetters({ tasks: 'tasks/getTasks', uiFlags: 'tasks/getTasksUIFlags', agents: 'agents/getAgents' }),
    isFetching() { return this.uiFlags && this.uiFlags.isFetching; },
    filteredTasks() {
      let result = this.tasks || [];
      const ids = this.filters.label_ids || [];
      if (ids.length) {
        result = result.filter(t => (t.labels || []).some(l => ids.includes(l.id)));
      }
      if (this.filters.blocked_only) {
        result = result.filter(t => t.has_blockers);
      }
      return result;
    },
    allSelected() {
      return this.filteredTasks.length > 0 && this.filteredTasks.every(t => this.selectedTaskIds.includes(t.id));
    },
    snoozeOptions() {
      return [
        { key: 'tomorrow', label: 'Amanhã 9h', days: 1, hour: 9 },
        { key: 'next_week', label: 'Próxima semana', days: 7, hour: 9 },
        { key: 'next_month', label: 'Próximo mês', days: 30, hour: 9 },
        { key: 'two_hours', label: 'Em 2 horas', days: 0, hour: 'plus2h' },
      ];
    },
    statusOptions() {
      return [
        { key: 'pending', label: 'Pendente', color: '#94a3b8' },
        { key: 'in_progress', label: 'Em andamento', color: '#3b82f6' },
        { key: 'done', label: 'Concluida', color: '#10b981' },
        { key: 'cancelled', label: 'Cancelada', color: '#ef4444' },
      ];
    },
    modalTabs() {
      const all = [
        { key: 'details', icon: 'i-ri-edit-box-line', label: 'Detalhes' },
        { key: 'subtasks', icon: 'i-ri-list-check', label: 'Subtarefas', count: this.currentSubtasks.length },
        { key: 'attachments', icon: 'i-ri-attachment-line', label: 'Anexos', count: this.attachments.length },
        { key: 'time', icon: 'i-ri-time-line', label: 'Tempo' },
        { key: 'deps', icon: 'i-ri-link', label: 'Dependencias', count: this.blockingTasks.length },
        { key: 'activity', icon: 'i-ri-chat-1-line', label: 'Atividade', count: this.currentComments.length },
      ];
      return all;
    },
    activeFiltersCount() {
      let n = 0;
      if (this.filters.status_key) n++;
      if (this.filters.priority) n++;
      if (this.filters.assigned_to_user_id) n++;
      if (this.filters.label_ids && this.filters.label_ids.length > 0) n++;
      if (this.filters.blocked_only) n++;
      return n;
    },
    filteredBlockerCandidates() {
      const q = (this.newBlockerSearch || '').toLowerCase();
      if (!q || q.length < 2) return [];
      const editingId = this.editingTask ? this.editingTask.id : null;
      const blockingIds = new Set(this.blockingTasks.map(t => t.id));
      return (this.tasks || []).filter(t => t.id !== editingId && !blockingIds.has(t.id) && (t.title || '').toLowerCase().includes(q)).slice(0, 8);
    },
    overdueStats() {
      const now = new Date();
      return (this.tasks || []).filter(t => t.due_at && new Date(t.due_at) < now && !(t.status && t.status.is_terminal)).length;
    },
    todayStats() {
      const today = new Date();
      const yyyy = today.getFullYear(), mm = today.getMonth(), dd = today.getDate();
      return (this.tasks || []).filter(t => {
        if (!t.due_at) return false;
        const d = new Date(t.due_at);
        return d.getFullYear() === yyyy && d.getMonth() === mm && d.getDate() === dd;
      }).length;
    },
    inProgressStats() {
      return (this.tasks || []).filter(t => t.status_key === 'in_progress').length;
    },
    boardColumns() {
      return [
        { key: 'pending', label: 'Pendente', color: '#94a3b8' },
        { key: 'in_progress', label: 'Em andamento', color: '#3b82f6' },
        { key: 'done', label: 'Concluída', color: '#10b981' },
        { key: 'cancelled', label: 'Cancelada', color: '#ef4444' },
      ];
    },
    tasksByStatus() {
      const map = { pending: [], in_progress: [], done: [], cancelled: [] };
      (this.filteredTasks || []).forEach(t => {
        const k = t.status_key || 'pending';
        if (map[k]) map[k].push(t);
      });
      return map;
    },
    agendaDays() {
      const days = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayLabelsBR = ['DOM','SEG','TER','QUA','QUI','SEX','SAB'];
      const monthsBR = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
      const buckets = {};
      (this.filteredTasks || []).forEach(t => {
        if (!t.due_at) return;
        const d = new Date(t.due_at);
        if (isNaN(d)) return;
        const iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        if (!buckets[iso]) buckets[iso] = [];
        buckets[iso].push(t);
      });
      const overdue = [];
      Object.keys(buckets).forEach(iso => {
        const d = new Date(iso + 'T00:00:00');
        if (d < today) overdue.push(...buckets[iso]);
      });
      if (overdue.length > 0) {
        overdue.sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
        days.push({
          iso: 'overdue',
          dayLabel: 'ATRASADO',
          dateLabel: overdue.length + ' tarefa(s) em atraso',
          isToday: false,
          tasks: overdue,
        });
      }
      for (let i = 0; i < 14; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        const tasks = (buckets[iso] || []).slice().sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
        days.push({
          iso,
          dayLabel: dayLabelsBR[d.getDay()],
          dateLabel: String(d.getDate()).padStart(2, '0') + ' ' + monthsBR[d.getMonth()],
          isToday: i === 0,
          tasks,
        });
      }
      return days;
    },
    agendaTasksWithoutDate() {
      return (this.filteredTasks || []).filter(t => !t.due_at);
    },
    reminderOptions() {
      return [
        { value: 5, label: '5 min' },
        { value: 15, label: '15 min' },
        { value: 60, label: '1 hora' },
        { value: 1440, label: '1 dia' },
      ];
    },
    filteredHashtagLabels() {
      const q = (this.hashtagFilter || '').toLowerCase();
      return (this.allLabels || []).filter(l => !q || (l.name || '').toLowerCase().includes(q)).slice(0, 6);
    },
    filteredMentionAgents() {
      const q = (this.mentionFilter || '').toLowerCase();
      return (this.agents || [])
        .filter(a => !q || (a.name || '').toLowerCase().includes(q))
        .slice(0, 6);
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchTasks();
    this.loadLabels();
    this.handleQueryParams();
    this.loadPresets();
    this._kbHandler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (this.modalOpen) return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); this.openCreate(); }
      else if (e.key === '/') {
        e.preventDefault();
        const search = document.querySelector('input[placeholder="Buscar..."]');
        if (search) search.focus();
      }
    };
    window.addEventListener('keydown', this._kbHandler);
  },
  beforeUnmount() {
    if (this._kbHandler) window.removeEventListener('keydown', this._kbHandler);
  },
  watch: {
    '$route.query'() { this.handleQueryParams(); },
  },
  methods: {
    fetchTasks() {
      const params = {};
      Object.keys(this.filters).forEach(k => {
        if (k === 'label_ids') return; // filtro de etiqueta eh client-side
        const v = this.filters[k];
        if (!Array.isArray(v) && v !== '' && v !== null) { params[k] = v; }
      });
      this.$store.dispatch('tasks/fetch', params);
    },
    debouncedFetch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchTasks(), 300);
    },
    openCreate() {
      this.editingTask = null;
      this.form = { title: '', description: '', due_at: '', priority: 'medium', status_key: 'pending', assigned_to_user_id: null, recurrence_rule: null, reminder_offsets: [], conversation_display_id: null, contact_id: null, contact_name: null };
      this.recurrenceEnabled = false;
      this.recurrenceFrequency = 'daily';
      this.recurrenceInterval = 1;
      this.selectedTemplateId = null;
      this.modalOpen = true;
      this.loadAvailableTemplates();
    },
    handleQueryParams() {
      const q = this.$route.query || {};
      if (q.preset) {
        this.applyBuiltInPreset(q.preset);
      }
      if (q.new === '1') {
        const convId = q.conversation_display_id ? Number(q.conversation_display_id) : null;
        this.editingTask = null;
        this.form = {
          title: '',
          description: '',
          due_at: '',
          priority: 'medium',
          status_key: 'pending',
          assigned_to_user_id: null,
          conversation_display_id: convId,
        };
        this.modalOpen = true;
        this.$router.replace({ name: 'tasks', params: this.$route.params, query: {} });
      } else if (q.open) {
        const id = Number(q.open);
        // Se tarefas ja carregadas, abre modal direto
        const t = (this.tasks || []).find(x => x.id === id);
        if (t) {
          this.openEdit(t);
        } else {
          // Senao, fetch e tenta de novo
          this.fetchTasks();
          setTimeout(() => {
            const t2 = (this.tasks || []).find(x => x.id === id);
            if (t2) this.openEdit(t2);
          }, 600);
        }
        this.$router.replace({ name: 'tasks', params: this.$route.params, query: {} });
      }
    },
    openEdit(t) {
      this.editingTask = t;
      // Scroll pra comments se foi pelo click no comment count
      if (this.pendingScrollToComments) {
        this.$nextTick(() => {
          setTimeout(() => {
            const target = document.querySelector('[data-comments-anchor]');
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.pendingScrollToComments = false;
          }, 300);
        });
      }
      this.form = {
        title: t.title || '',
        description: t.description || '',
        due_at: t.due_at ? this.toBrDatetime(t.due_at) : '',
        priority: t.priority || 'medium',
        status_key: t.status_key || 'pending',
        assigned_to_user_id: t.assigned_to_user_id || null,
        recurrence_rule: t.recurrence_rule || null,
        reminder_offsets: Array.isArray(t.reminder_offsets) ? t.reminder_offsets.slice() : [],
        conversation_display_id: t.conversation_display_id || null,
        contact_id: t.contact_id || (t.contact && t.contact.id) || null,
        contact_name: (t.contact && t.contact.name) || null,
      };
      const r = t.recurrence_rule || null;
      this.recurrenceEnabled = !!r;
      this.recurrenceFrequency = (r && r.frequency) || 'daily';
      this.recurrenceInterval = (r && r.interval) || 1;
      this.currentSubtasks = [];
      this.currentComments = [];
      this.currentTaskLabels = (t.labels || []).map(l => l.id);
      this.modalOpen = true;
      this.loadFullTask();
      this.loadLabels();
    },
    closeModal() {
      this.modalOpen = false;
      this.editingTask = null;
      this.stopLiveTick();
      this.activeTimer = null;
      this.timerActive = false;
      this.totalTrackedSeconds = 0;
      this.blockingTasks = [];
      this.blockedTasks = [];
      this.newBlockerSearch = '';
      this.timeEntries = [];
      this.showTimeHistory = false;
      this.pendingSubtasksFromTemplate = [];
      this.attachments = [];
    },
    async saveTask() {
      this.saving = true;
      try {
        const payload = Object.assign({}, this.form);
        payload.due_at = payload.due_at ? this.parseBrDatetime(payload.due_at) : null;
        payload.recurrence_rule = this.recurrenceEnabled ? { frequency: this.recurrenceFrequency, interval: Math.max(1, Number(this.recurrenceInterval) || 1) } : null;
        payload.label_ids = this.currentTaskLabels;
        let createdTask = null;
        if (this.editingTask) {
          await this.$store.dispatch('tasks/update', { id: this.editingTask.id, payload });
          useAlert('Tarefa atualizada');
        } else {
          createdTask = await this.$store.dispatch('tasks/create', payload);
          if (createdTask && createdTask.id && (this.pendingSubtasksFromTemplate || []).length > 0) {
            for (const st of this.pendingSubtasksFromTemplate) {
              try { await TaskSubtasksAPI.create(createdTask.id, { title: st.title, position: st.position }); } catch (e) { /* ignore */ }
            }
          }
          useAlert('Tarefa criada');
        }
        this.closeModal();
        this.fetchTasks();
      } catch (err) {
        useAlert('Erro ao salvar tarefa');
      } finally {
        this.saving = false;
      }
    },
    async toggleComplete(t) {
      try {
        if (this.isDone(t)) {
          await this.$store.dispatch('tasks/reopen', t.id);
        } else {
          if (t.has_blockers && !window.confirm('Esta tarefa esta bloqueada por outras dependencias. Concluir mesmo assim?')) return;
          await this.$store.dispatch('tasks/complete', t.id);
        }
        this.fetchTasks();
      } catch (err) {
        useAlert('Erro ao atualizar tarefa');
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedTaskIds = [];
      } else {
        this.selectedTaskIds = this.filteredTasks.map(t => t.id);
      }
    },
    toggleSelectTask(id) {
      const i = this.selectedTaskIds.indexOf(id);
      if (i >= 0) this.selectedTaskIds.splice(i, 1);
      else this.selectedTaskIds.push(id);
    },
    async bulkComplete() {
      const ids = this.selectedTaskIds.slice();
      try {
        await Promise.all(ids.map(id => this.$store.dispatch('tasks/complete', id).catch(() => null)));
        this.selectedTaskIds = [];
        this.fetchTasks();
        useAlert(ids.length + ' tarefa(s) concluida(s)');
      } catch (e) { useAlert('Erro'); }
    },
    async bulkSetStatus(statusKey) {
      const ids = this.selectedTaskIds.slice();
      try {
        await Promise.all(ids.map(id => this.$store.dispatch('tasks/update', { id, payload: { status_key: statusKey } }).catch(() => null)));
        this.selectedTaskIds = [];
        this.fetchTasks();
        useAlert(ids.length + ' tarefa(s) atualizada(s)');
      } catch (e) { useAlert('Erro'); }
    },
    async bulkDelete() {
      const ids = this.selectedTaskIds.slice();
      if (!window.confirm('Excluir ' + ids.length + ' tarefa(s)?')) return;
      try {
        await Promise.all(ids.map(id => this.$store.dispatch('tasks/remove', id).catch(() => null)));
        this.selectedTaskIds = [];
        this.fetchTasks();
        useAlert(ids.length + ' tarefa(s) excluida(s)');
      } catch (e) { useAlert('Erro'); }
    },
    priorityHex(p) {
      return ({ urgent: '#dc2626', high: '#ea580c', medium: '#2563eb', low: '#94a3b8', none: '#cbd5e1' })[p] || '#cbd5e1';
    },
    priorityLabel(p) {
      return ({ urgent: 'Urgente', high: 'Alta', medium: 'Media', low: 'Baixa', none: 'Sem prioridade' })[p] || p;
    },
    statusPillStyleForKey(key) {
      const opt = (this.statusOptions || []).find(o => o.key === key);
      const color = (opt && opt.color) || '#64748b';
      return { backgroundColor: color + '22', color: color, borderColor: color + '55' };
    },
    avatarColor(id) {
      const colors = ['#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#059669', '#0891b2'];
      return colors[Math.abs(Number(id) || 0) % colors.length];
    },
    avatarStyle(user) {
      if (!user) return { backgroundColor: '#94a3b8' };
      const url = user.avatar_url || user.thumbnail;
      if (url && !url.endsWith('default.png')) {
        return { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' };
      }
      return { backgroundColor: this.avatarColor(user.id || 0) };
    },
    avatarInitial(user) {
      if (!user) return '?';
      const url = user.avatar_url || user.thumbnail;
      if (url && !url.endsWith('default.png')) return '';
      return ((user.name || '?')[0] || '?').toUpperCase();
    },
    onContactSearchInput() {
      if (this.contactSearchTimer) clearTimeout(this.contactSearchTimer);
      this.contactSearchTimer = setTimeout(() => this.runContactSearch(), 250);
    },
    async runContactSearch() {
      const q = (this.contactSearchQuery || "").trim();
      if (q.length < 2) { this.contactSearchResults = []; return; }
      this.contactSearchLoading = true;
      try {
        const ContactAPI = (await import("dashboard/api/contacts")).default;
        const r = await ContactAPI.search(q, 1);
        const list = (r.data && r.data.payload) || r.data || [];
        this.contactSearchResults = (Array.isArray(list) ? list : (list.data ? list.data : [])).slice(0, 8);
      } catch (e) { this.contactSearchResults = []; }
      finally { this.contactSearchLoading = false; }
    },
    async pickContact(c) {
      this.form.contact_id = c.id;
      this.form.contact_name = c.name || c.phone_number || ("Contato #" + c.id);
      this.contactSearchOpen = false;
      this.contactSearchQuery = "";
      this.contactSearchResults = [];
      try {
        const accountId = (this.$route && this.$route.params && this.$route.params.accountId) || 1;
        const r = await axios.get(`/api/v1/accounts/${accountId}/contacts/${c.id}/conversations`);
        const convs = (r.data && r.data.payload) || r.data || [];
        const list = Array.isArray(convs) ? convs : (convs.data || []);
        const latest = list.find(x => x.status === "open") || list[0];
        if (latest && (latest.id || latest.display_id)) {
          this.form.conversation_display_id = latest.display_id || latest.id;
        }
      } catch (e) { /* silent */ }
    },
    clearContactLink() {
      this.form.contact_id = null;
      this.form.contact_name = null;
      this.form.conversation_display_id = null;
    },
    conversationLink(displayId) {
      if (!displayId) return null;
      const accountId = this.$route?.params?.accountId || 1;
      return `/app/accounts/${accountId}/conversations/${displayId}`;
    },
    copyShareLink() {
      if (!this.editingShareUrl) return;
      navigator.clipboard?.writeText(this.editingShareUrl);
      useAlert('Link copiado');
    },
    onModalDragover() { this.dragOver = true; },
    onModalDragleave(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) this.dragOver = false;
    },
    async onModalDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!file || !this.editingTask) return;
      this.activeTab = 'attachments';
      this.attachmentUploading = true;
      try {
        const TaskAttachmentsAPI = (await import('dashboard/api/taskAttachments')).default;
        const { data } = await TaskAttachmentsAPI.upload(this.editingTask.id, file);
        this.attachments.unshift(data);
        useAlert('Anexo enviado');
      } catch (err) { useAlert('Erro ao enviar anexo'); }
      finally { this.attachmentUploading = false; }
    },
    attachmentLucideIcon(contentType) {
      const ct = (contentType || '').toLowerCase();
      if (ct.startsWith('image/')) return 'i-ri-image-line';
      if (ct.startsWith('video/')) return 'i-ri-video-line';
      if (ct.startsWith('audio/')) return 'i-ri-music-line';
      if (ct.includes('pdf')) return 'i-ri-file-text-line';
      if (ct.includes('zip') || ct.includes('rar') || ct.includes('7z') || ct.includes('tar')) return 'i-ri-archive-line';
      if (ct.includes('spreadsheet') || ct.includes('excel') || ct.includes('csv')) return 'i-ri-file-list-3-line';
      if (ct.includes('word') || ct.includes('document')) return 'i-ri-file-text-line';
      return 'i-ri-file-line';
    },
    attachmentIconClass(contentType) {
      const ct = (contentType || '').toLowerCase();
      if (ct.startsWith('image/')) return 'ts-att-icon-blue';
      if (ct.startsWith('video/')) return 'ts-att-icon-purple';
      if (ct.startsWith('audio/')) return 'ts-att-icon-emerald';
      if (ct.includes('pdf')) return 'ts-att-icon-red';
      if (ct.includes('zip') || ct.includes('rar') || ct.includes('7z') || ct.includes('tar')) return 'ts-att-icon-amber';
      if (ct.includes('spreadsheet') || ct.includes('excel') || ct.includes('csv')) return 'ts-att-icon-emerald';
      return 'ts-att-icon-slate';
    },
    resetFilters() {
      this.filters = { q: '', status_key: '', priority: '', assigned_to_user_id: '', sort: 'due_at_asc', label_ids: [], blocked_only: false };
      this.activePresetId = null;
      this.fetchTasks();
    },
    priorityBorderClass(p) {
      const map = {
        urgent: 'border-l-red-500',
        high: 'border-l-orange-400',
        medium: 'border-l-blue-400',
        low: 'border-l-slate-300',
        none: 'border-l-slate-200',
      };
      return map[p] || 'border-l-transparent';
    },
    async duplicateTask(t) {
      try {
        const payload = {
          title: t.title + ' (copia)',
          description: t.description,
          due_at: t.due_at,
          priority: t.priority,
          status_key: 'pending',
          assigned_to_user_id: t.assigned_to_user_id,
        };
        await this.$store.dispatch('tasks/create', payload);
        this.fetchTasks();
        useAlert('Tarefa duplicada');
      } catch (e) { useAlert('Erro ao duplicar'); }
    },
    async quickDelete(t) {
      if (!window.confirm(`Excluir "${t.title}"?`)) return;
      try {
        await this.$store.dispatch('tasks/remove', t.id);
        useAlert('Tarefa excluida');
        this.fetchTasks();
      } catch (e) { useAlert('Erro'); }
    },
    async deleteTask() {
      if (!this.editingTask) return;
      if (!window.confirm('Excluir essa tarefa permanentemente?')) return;
      try {
        await this.$store.dispatch('tasks/remove', this.editingTask.id);
        useAlert('Tarefa excluída');
        this.closeModal();
        this.fetchTasks();
      } catch (err) {
        useAlert('Erro ao excluir tarefa');
      }
    },
    async onBoardChange(evt, newStatusKey) {
      if (!evt.added) return;
      const t = evt.added.element;
      if (t.status_key === newStatusKey) return;
      try {
        await this.$store.dispatch('tasks/update', { id: t.id, payload: { status_key: newStatusKey } });
        this.fetchTasks();
      } catch (e) {
        useAlert('Erro ao mover tarefa');
        this.fetchTasks();
      }
    },
    async loadFullTask() {
      try {
        // eslint-disable-next-line no-undef
        const { data } = await axios.get(`/api/v1/accounts/${this.$route.params.accountId}/tasks/${this.editingTask.id}`);
        this.currentSubtasks = data.subtasks || [];
        this.currentComments = data.comments || [];
        this.currentActivities = data.activities || [];
        this.editingShareUrl = data.public_token ? (window.location.origin + '/public/tasks/' + data.public_token) : '';
        this.totalTrackedSeconds = data.total_tracked_seconds || 0;
        this.blockingTasks = data.blocking_tasks || [];
        this.blockedTasks = data.blocked_tasks || [];
        this.attachments = data.attachments || [];
        await this.loadActiveTimer();
      } catch (e) {
        // silent
      }
    },
    async loadActiveTimer() {
      try {
        const TaskTimeEntriesAPI = (await import('dashboard/api/taskTimeEntries')).default;
        const { data } = await TaskTimeEntriesAPI.list(this.editingTask.id);
        this.totalTrackedSeconds = data.total_seconds || 0;
        this.timeEntries = Array.isArray(data.entries) ? data.entries : [];
        const active = data.active_for_me;
        this.activeTimer = active;
        this.timerActive = !!active;
        this.startLiveTick();
      } catch (e) {
        this.activeTimer = null;
        this.timerActive = false;
        this.timeEntries = [];
      }
    },
    async onAttachmentSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file || !this.editingTask) return;
      this.attachmentUploading = true;
      try {
        const TaskAttachmentsAPI = (await import('dashboard/api/taskAttachments')).default;
        const { data } = await TaskAttachmentsAPI.upload(this.editingTask.id, file);
        this.attachments.unshift(data);
        useAlert('Anexo enviado');
      } catch (err) { useAlert('Erro ao enviar anexo'); }
      finally {
        this.attachmentUploading = false;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      }
    },
    async removeAttachment(att) {
      if (!window.confirm(`Remover anexo "${att.name}"?`)) return;
      try {
        const TaskAttachmentsAPI = (await import('dashboard/api/taskAttachments')).default;
        await TaskAttachmentsAPI.remove(this.editingTask.id, att.id);
        this.attachments = this.attachments.filter(a => a.id !== att.id);
        useAlert('Anexo removido');
      } catch (err) { useAlert('Erro ao remover'); }
    },
    formatBytes(bytes) {
      if (!bytes) return '';
      const u = ['B', 'KB', 'MB', 'GB'];
      let i = 0; let n = Number(bytes);
      while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
      return n.toFixed(n >= 10 || i === 0 ? 0 : 1) + u[i];
    },
    async removeTimeEntry(entry) {
      if (!window.confirm('Remover este registro de tempo?')) return;
      try {
        const TaskTimeEntriesAPI = (await import('dashboard/api/taskTimeEntries')).default;
        await TaskTimeEntriesAPI.remove(this.editingTask.id, entry.id);
        this.timeEntries = this.timeEntries.filter(e => e.id !== entry.id);
        this.totalTrackedSeconds -= (entry.duration_seconds || 0);
        useAlert('Registro removido');
      } catch (e) { useAlert('Erro ao remover'); }
    },
    startLiveTick() {
      if (this.liveTickHandle) clearInterval(this.liveTickHandle);
      this.liveTickSeconds = 0;
      if (!this.timerActive || !this.activeTimer) return;
      const startedAt = new Date(this.activeTimer.started_at).getTime();
      this.liveTickHandle = setInterval(() => {
        this.liveTickSeconds = Math.floor((Date.now() - startedAt) / 1000);
      }, 1000);
    },
    stopLiveTick() {
      if (this.liveTickHandle) { clearInterval(this.liveTickHandle); this.liveTickHandle = null; }
      this.liveTickSeconds = 0;
    },
    async startTimer() {
      try {
        const TaskTimeEntriesAPI = (await import('dashboard/api/taskTimeEntries')).default;
        const { data } = await TaskTimeEntriesAPI.start(this.editingTask.id);
        this.activeTimer = data;
        this.timerActive = true;
        this.startLiveTick();
        useAlert('Timer iniciado');
      } catch (e) { useAlert('Erro ao iniciar timer'); }
    },
    async stopTimer() {
      if (!this.activeTimer) return;
      try {
        const TaskTimeEntriesAPI = (await import('dashboard/api/taskTimeEntries')).default;
        const { data } = await TaskTimeEntriesAPI.stop(this.editingTask.id, this.activeTimer.id);
        this.totalTrackedSeconds += (data.duration_seconds || 0);
        this.activeTimer = null;
        this.timerActive = false;
        this.stopLiveTick();
        useAlert('Timer parado');
      } catch (e) { useAlert('Erro ao parar timer'); }
    },
    formatHMS(secs) {
      const s = Math.max(0, parseInt(secs || 0, 10));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const r = s % 60;
      return [h, m, r].map(n => String(n).padStart(2, '0')).join(':');
    },
    async addBlockingDep(t) {
      try {
        const TaskDependenciesAPI = (await import('dashboard/api/taskDependencies')).default;
        const { data } = await TaskDependenciesAPI.add(this.editingTask.id, t.id);
        this.blockingTasks.push({ id: t.id, title: t.title, status_key: t.status_key, due_at: t.due_at, dep_id: data.id });
        this.newBlockerSearch = '';
        useAlert('Dependencia adicionada');
      } catch (e) { useAlert('Erro: ' + (e.response?.data?.errors?.[0] || 'falha')); }
    },
    async removeDependency(depId) {
      if (!window.confirm('Remover dependencia?')) return;
      try {
        const TaskDependenciesAPI = (await import('dashboard/api/taskDependencies')).default;
        await TaskDependenciesAPI.remove(this.editingTask.id, depId);
        this.blockingTasks = this.blockingTasks.filter(t => (t.dep_id || t.id) !== depId);
        useAlert('Dependencia removida');
      } catch (e) { useAlert('Erro ao remover'); }
    },

    openShareDialog() {
      this.showShareDialog = true;
    },
    onShareChanged(payload) {
      this.editingShareUrl = payload.public_url || '';
    },
    async loadAvailableTemplates() {
      try {
        const TaskTemplatesAPI = (await import('dashboard/api/taskTemplates')).default;
        const { data } = await TaskTemplatesAPI.list();
        this.availableTemplates = Array.isArray(data) ? data : [];
      } catch (e) { /* silent */ }
    },
    applyTemplateInline() {
      if (!this.selectedTemplateId) return;
      const t = this.availableTemplates.find(x => x.id === this.selectedTemplateId);
      if (!t) return;
      this.form.title = this.form.title || t.name;
      this.form.description = this.form.description || t.description || '';
      this.form.priority = ['none','low','medium','high','urgent'][t.default_priority] || this.form.priority;
      this.currentTaskLabels = (t.default_label_ids || []).slice();
      this.pendingSubtasksFromTemplate = (t.default_subtasks || []).map((st, i) => ({ title: st.title || st['title'] || '', position: typeof st.position !== 'undefined' ? st.position : i })).filter(x => x.title);
    },
    onTemplateApplied(task) {
      this.fetchTasks();
      if (task && task.id) {
        setTimeout(() => {
          axios.get(`/api/v1/accounts/${this.$route.params.accountId}/tasks/${task.id}`).then(r => {
            this.openEdit(r.data);
          }).catch(() => {});
        }, 400);
      }
      this.showTemplatesManager = false;
    },
    async loadLabels() {
      try {
        const { data } = await TaskLabelsAPI.list();
        this.allLabels = Array.isArray(data) ? data : [];
      } catch (e) {
        this.allLabels = [];
      }
    },
    async addSubtask() {
      if (!this.newSubtaskTitle.trim() || !this.editingTask) return;
      this.savingSubtask = true;
      try {
        const { data } = await TaskSubtasksAPI.create(this.editingTask.id, { title: this.newSubtaskTitle.trim() });
        this.currentSubtasks.push(data);
        this.newSubtaskTitle = '';
      } catch (e) {
        useAlert('Erro ao criar subtarefa');
      } finally {
        this.savingSubtask = false;
      }
    },
    async toggleSubtask(s) {
      try {
        const { data } = await TaskSubtasksAPI.toggle(this.editingTask.id, s.id);
        const idx = this.currentSubtasks.findIndex(x => x.id === s.id);
        if (idx >= 0) this.currentSubtasks.splice(idx, 1, data);
      } catch (e) { useAlert('Erro'); }
    },
    async removeSubtask(s) {
      try {
        await TaskSubtasksAPI.remove(this.editingTask.id, s.id);
        this.currentSubtasks = this.currentSubtasks.filter(x => x.id !== s.id);
      } catch (e) { useAlert('Erro'); }
    },
    onDescriptionInput(e) {
      const value = e.target.value;
      const cursor = e.target.selectionStart || 0;
      const before = value.slice(0, cursor);
      const m = before.match(/(?:^|\s)#(\w*)$/);
      if (m) {
        this.hashtagOpen = true;
        this.hashtagFilter = m[1];
        this.hashtagStartIdx = cursor - m[1].length - 1;
        this.hashtagActiveIdx = 0;
      } else {
        this.hashtagOpen = false;
      }
    },
    onDescriptionKeydown(e) {
      if (!this.hashtagOpen) return;
      const list = this.filteredHashtagLabels;
      if (e.key === 'ArrowDown') { e.preventDefault(); this.hashtagActiveIdx = (this.hashtagActiveIdx + 1) % list.length; }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.hashtagActiveIdx = (this.hashtagActiveIdx - 1 + list.length) % list.length; }
      else if (e.key === 'Enter' && list.length > 0) { e.preventDefault(); this.selectHashtag(list[this.hashtagActiveIdx]); }
      else if (e.key === 'Escape') { this.hashtagOpen = false; }
    },
    selectHashtag(label) {
      const start = this.hashtagStartIdx;
      const cur = this.$refs.descTextarea?.selectionStart || (this.form.description || '').length;
      const before = (this.form.description || '').slice(0, start);
      const after = (this.form.description || '').slice(cur);
      const insertion = '#' + label.name + ' ';
      this.form.description = before + insertion + after;
      this.hashtagOpen = false;
      // Auto-attach label se ainda nao incluida
      if (!this.currentTaskLabels.includes(label.id)) this.currentTaskLabels.push(label.id);
    },
    onCommentInput(e) {
      const value = e.target.value;
      const cursor = e.target.selectionStart || 0;
      const before = value.slice(0, cursor);
      const m = before.match(/(?:^|\s)@(\w*)$/);
      if (m) {
        this.mentionOpen = true;
        this.mentionFilter = m[1];
        this.mentionStartIdx = cursor - m[1].length - 1;
        this.mentionActiveIdx = 0;
      } else {
        this.mentionOpen = false;
      }
    },
    onCommentKeydown(e) {
      if (!this.mentionOpen) return;
      const list = this.filteredMentionAgents;
      if (e.key === 'ArrowDown') { e.preventDefault(); this.mentionActiveIdx = (this.mentionActiveIdx + 1) % list.length; }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.mentionActiveIdx = (this.mentionActiveIdx - 1 + list.length) % list.length; }
      else if (e.key === 'Enter' && list.length > 0) { e.preventDefault(); this.selectMention(list[this.mentionActiveIdx]); }
      else if (e.key === 'Escape') { this.mentionOpen = false; }
    },
    selectMention(agent) {
      const start = this.mentionStartIdx;
      const cur = this.$refs.commentTextarea?.selectionStart || this.newCommentContent.length;
      const before = this.newCommentContent.slice(0, start);
      const after = this.newCommentContent.slice(cur);
      const insertion = '@' + agent.name + ' ';
      this.newCommentContent = before + insertion + after;
      this.mentionOpen = false;
      this.mentionFilter = '';
      this.$nextTick(() => {
        const el = this.$refs.commentTextarea;
        if (el) {
          el.focus();
          const pos = (before + insertion).length;
          el.setSelectionRange(pos, pos);
        }
      });
    },
    renderCommentContent(text) {
      if (!text) return '';
      let out = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const names = (this.agents || []).map(a => a.name).filter(Boolean).sort((a, b) => b.length - a.length);
      names.forEach(n => {
        const target = '@' + n;
        out = out.split(target).join('<span class="text-n-brand dark:text-blue-400 font-semibold">' + target + '</span>');
      });
      return out;
    },
    startEditComment(c) {
      this.editingCommentId = c.id;
      this.editingCommentContent = c.content;
    },
    async saveCommentEdit() {
      if (!this.editingCommentId) return;
      try {
        // eslint-disable-next-line no-undef
        const { data } = await axios.patch(`/api/v1/accounts/${this.$route.params.accountId}/tasks/${this.editingTask.id}/comments/${this.editingCommentId}`, { content: this.editingCommentContent.trim() });
        const idx = this.currentComments.findIndex(x => x.id === this.editingCommentId);
        if (idx >= 0) this.currentComments.splice(idx, 1, data);
        this.editingCommentId = null;
      } catch (e) { useAlert('Erro ao editar'); }
    },
    async addComment() {
      if (!this.newCommentContent.trim() || !this.editingTask) return;
      this.savingComment = true;
      try {
        const text = this.newCommentContent.trim();
        const mentioned = [];
        (this.agents || []).forEach(a => {
          if (a.name && text.includes('@' + a.name)) {
            mentioned.push(a.id);
          }
        });
        const { data } = await TaskCommentsAPI.create(this.editingTask.id, { content: text, mentioned_user_ids: mentioned });
        this.currentComments.push(data);
        this.newCommentContent = '';
      } catch (e) {
        useAlert('Erro ao comentar');
      } finally {
        this.savingComment = false;
      }
    },
    async removeComment(c) {
      if (!window.confirm('Remover comentário?')) return;
      try {
        await TaskCommentsAPI.remove(this.editingTask.id, c.id);
        this.currentComments = this.currentComments.filter(x => x.id !== c.id);
      } catch (e) { useAlert('Erro'); }
    },
    toggleTaskLabel(labelId) {
      const idx = this.currentTaskLabels.indexOf(labelId);
      if (idx >= 0) this.currentTaskLabels.splice(idx, 1);
      else this.currentTaskLabels.push(labelId);
    },
    formatRelativeTime(iso) {
      try {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / 1000);
        if (diff < 60) return 'agora';
        if (diff < 3600) return Math.floor(diff/60) + ' min atrás';
        if (diff < 86400) return Math.floor(diff/3600) + 'h atrás';
        return Math.floor(diff/86400) + 'd atrás';
      } catch (e) { return ''; }
    },
    async snoozeTask(t, days, hour) {
      this.snoozeForTaskId = null;
      let target;
      if (hour === 'plus2h') {
        target = new Date(Date.now() + 2 * 3600 * 1000);
      } else {
        target = new Date();
        target.setDate(target.getDate() + days);
        target.setHours(hour || 9, 0, 0, 0);
      }
      try {
        await this.$store.dispatch('tasks/update', { id: t.id, payload: { due_at: target.toISOString() } });
        this.fetchTasks();
        useAlert('Tarefa adiada');
      } catch (e) { useAlert('Erro'); }
    },
    async quickSetStatus(t, statusKey) {
      this.statusChangerForTaskId = null;
      try {
        await this.$store.dispatch('tasks/update', { id: t.id, payload: { status_key: statusKey } });
        this.fetchTasks();
      } catch (e) { useAlert('Erro'); }
    },
    applyBuiltInPreset(preset) {
      const currentUser = this.$store.getters.getCurrentUser;
      const userId = currentUser?.id;
      switch (preset) {
        case 'today':
          this.filters = { q: '', status_key: 'pending', priority: '', assigned_to_user_id: '', sort: 'due_at_asc', label_ids: [] };
          this.viewMode = 'list';
          this.fetchTasks();
          break;
        case 'overdue':
          this.filters = { q: '', status_key: 'pending', priority: '', assigned_to_user_id: '', sort: 'due_at_asc', label_ids: [] };
          this.viewMode = 'list';
          this.fetchTasks();
          break;
        case 'mine':
          this.filters = { q: '', status_key: '', priority: '', assigned_to_user_id: userId || '', sort: 'due_at_asc', label_ids: [] };
          this.viewMode = 'list';
          this.fetchTasks();
          break;
        case 'done':
          this.filters = { q: '', status_key: 'done', priority: '', assigned_to_user_id: '', sort: 'created_desc', label_ids: [] };
          this.viewMode = 'list';
          this.fetchTasks();
          break;
        case 'blocked':
          this.filters = { q: '', status_key: '', priority: '', assigned_to_user_id: '', sort: 'due_at_asc', label_ids: [], blocked_only: true };
          this.viewMode = 'list';
          this.fetchTasks();
          break;
      }
    },
    loadPresets() {
      try {
        const raw = localStorage.getItem('tasks_presets_v1');
        this.savedPresets = raw ? JSON.parse(raw) : [];
      } catch (e) { this.savedPresets = []; }
    },
    persistPresets() {
      try { localStorage.setItem('tasks_presets_v1', JSON.stringify(this.savedPresets)); } catch (e) {}
    },
    savePreset() {
      const name = window.prompt('Nome do preset:');
      if (!name || !name.trim()) return;
      const preset = { id: Date.now(), name: name.trim(), filters: JSON.parse(JSON.stringify(this.filters)), viewMode: this.viewMode };
      this.savedPresets.push(preset);
      this.persistPresets();
      this.activePresetId = preset.id;
      useAlert('Preset salvo');
    },
    applyPreset(p) {
      this.filters = JSON.parse(JSON.stringify(p.filters));
      this.viewMode = p.viewMode || 'list';
      this.activePresetId = p.id;
      this.fetchTasks();
    },
    clearPreset() {
      this.activePresetId = null;
      this.filters = { q: '', status_key: '', priority: '', assigned_to_user_id: '', sort: 'due_at_asc', label_ids: [] };
      this.fetchTasks();
    },
    deletePreset() {
      if (!this.activePresetId) return;
      if (!window.confirm('Remover este preset?')) return;
      this.savedPresets = this.savedPresets.filter(p => p.id !== this.activePresetId);
      this.persistPresets();
      this.activePresetId = null;
    },
    activityLabel(action) {
      const map = {
        created: 'criou tarefa',
        updated: 'atualizou tarefa',
        completed: 'concluiu tarefa',
        reopened: 'reabriu tarefa',
        commented: 'comentou',
        subtask_added: 'adicionou subtarefa',
        subtask_completed: 'concluiu subtarefa',
        label_added: 'adicionou etiqueta',
        label_removed: 'removeu etiqueta',
      };
      return map[action] || action;
    },
    isDone(t) { return t.status && t.status.is_terminal; },
    titleClass(t) {
      const base = 'text-sm font-medium';
      return this.isDone(t) ? base + ' line-through text-n-slate-9' : base + ' text-n-slate-12';
    },
    labelStyle(l) { return { backgroundColor: (l.color || '#999') + '22', color: l.color || '#999' }; },
    statusStyle(t) {
      const c = (t.status && t.status.color) || '#6b7280';
      return { backgroundColor: c + '22', color: c };
    },
    statusLabel(t) { return (t.status && t.status.label) || t.status_key; },
    toBrDatetime(iso) {
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear() + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
      } catch (e) {
        return '';
      }
    },
    parseBrDatetime(s) {
      // 'DD/MM/YYYY HH:mm' -> ISO string
      const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
      if (!m) return null;
      const [, dd, mm, yyyy, hh, mi] = m;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi));
      return d.toISOString();
    },
    formatTime(iso) {
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        return pad(d.getHours()) + ':' + pad(d.getMinutes());
      } catch (e) { return ''; }
    },
    formatDate(iso) {
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        const today = new Date();
        const sameDay = d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
        if (sameDay) return 'Hoje às ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
      } catch (e) {
        return iso;
      }
    },
    priorityColor(p) {
      const map = { urgent: 'bg-red-500', high: 'bg-orange-400', medium: 'bg-blue-400', low: 'bg-n-slate-8' };
      return map[p] || 'bg-n-slate-8';
    },
  },
};
</script>

<style>
.tasks-app select,
.tasks-app input[type="text"],
.kanban-tasks-tab select,
.kanban-tasks-tab input[type="text"] {
  background-position: right 12px center !important;
  padding-right: 32px !important;
}

/* DatePicker popup precisa ficar acima do modal-mask (z-index 9990) */
.mx-datepicker-popup {
  z-index: 9995 !important;
}

.mx-datepicker-main {
  z-index: 9995 !important;
}

/* ============================================================
   v220 UX overhaul - Modal redesign with tabs
   ============================================================ */
.ts-modal { display: flex; flex-direction: column; max-height: 90vh; position: relative; background: var(--color-background); }

.ts-modal-dropzone-overlay { position: absolute; inset: 0; z-index: 50; background: rgba(59,130,246,0.08); backdrop-filter: blur(2px); border: 3px dashed #3b82f6; border-radius: 12px; display: flex; align-items: center; justify-content: center; pointer-events: none; }
.ts-modal-dropzone-inner { background: var(--color-background); padding: 32px 48px; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.15); text-align: center; }
.dark .ts-modal-dropzone-inner { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }
.ts-modal-header { padding: 20px 24px 12px; border-bottom: 1px solid var(--n-weak, rgba(0,0,0,0.08)); }
.dark .ts-modal-header { border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-modal-header-row { display: flex; align-items: center; gap: 12px; }
.ts-modal-priority-bullet { width: 12px; height: 12px; border-radius: 999px; flex-shrink: 0; }
.ts-modal-title-input { flex: 1; font-size: 18px; font-weight: 600; color: var(--n-slate-12); background: transparent; border: none; outline: none; padding: 4px 0; }
.dark .ts-modal-title-input { color: var(--n-slate-12); }
.ts-modal-status-pill { font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 999px; border: 1px solid; cursor: pointer; background-position: right 8px center !important; padding-right: 24px !important; }
.ts-modal-header-meta { display: flex; align-items: center; gap: 16px; margin-top: 8px; font-size: 12px; color: var(--n-slate-10); }
.ts-modal-meta-item { display: inline-flex; align-items: center; gap: 4px; }
.ts-modal-actions { display: flex; gap: 4px; }
.ts-modal-template-bar { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding-top: 10px; border-top: 1px dashed var(--n-weak, rgba(0,0,0,0.08)); }
.dark .ts-modal-template-bar { border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-modal-template-select { font-size: 12px; padding: 4px 24px 4px 8px; border-radius: 6px; border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); background: var(--color-background); color: var(--n-slate-12); }
.dark .ts-modal-template-select { background: var(--n-alpha-2, rgba(0,0,0,0.08)); color: var(--n-slate-12); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-modal-tabs { display: flex; gap: 2px; padding: 0 16px; border-bottom: 1px solid var(--n-weak, rgba(0,0,0,0.08)); background: transparent; overflow-x: auto; }
.dark .ts-modal-tabs { background: var(--n-alpha-2, rgba(0,0,0,0.08)); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-modal-tab { display: inline-flex; align-items: center; gap: 6px; padding: 10px 14px; font-size: 13px; font-weight: 500; color: var(--n-slate-10); border-bottom: 2px solid transparent; white-space: nowrap; transition: color 150ms, border-color 150ms; }
.ts-modal-tab:hover { color: var(--n-slate-12); }
.ts-modal-tab.is-active { color: var(--color-woot, #1f93ff); border-bottom-color: var(--color-woot, #1f93ff); background: var(--color-background); }
.dark .ts-modal-tab.is-active { color: #60a5fa; border-bottom-color: #60a5fa; background: var(--color-background); }
.ts-modal-tab-count { font-size: 11px; background: #e2e8f0; color: var(--n-slate-10); padding: 1px 6px; border-radius: 999px; font-weight: 600; }
.ts-modal-tab.is-active .ts-modal-tab-count { background: #dbeafe; color: var(--color-woot, #1f93ff); }
.dark .ts-modal-tab-count { background: var(--n-alpha-3, rgba(0,0,0,0.12)); color: var(--n-slate-10); }
.ts-modal-body { flex: 1; overflow-y: auto; padding: 0; background: var(--color-background); width: 100%; align-self: stretch; }
.dark .ts-modal-body { background: var(--color-background); }
.ts-modal-pane { padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; width: 100%; align-items: stretch; }
.ts-modal-extra { padding: 0; }
.ts-modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 24px; border-top: 1px solid var(--n-weak, rgba(0,0,0,0.08)); background: transparent; }
.dark .ts-modal-footer { background: var(--n-alpha-2, rgba(0,0,0,0.08)); border-color: var(--n-strong, rgba(0,0,0,0.16)); }

/* Buttons */
.ts-btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; font-size: 14px; font-weight: 500; background: var(--color-woot, #1f93ff); color: white; border-radius: 8px; transition: background 120ms; }
.ts-btn-primary:hover:not(:disabled) { background: var(--color-woot, #1f93ff); filter: brightness(0.9); }
.ts-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.ts-btn-primary-sm { display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; font-size: 12px; font-weight: 500; background: var(--color-woot, #1f93ff); color: white; border-radius: 6px; }
.ts-btn-primary-sm:hover:not(:disabled) { background: var(--color-woot, #1f93ff); filter: brightness(0.9); }
.ts-btn-primary-sm:disabled { opacity: 0.5; }
.ts-btn-secondary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; font-size: 14px; font-weight: 500; background: var(--n-alpha-1, rgba(0,0,0,0.04)); color: var(--n-slate-11); border-radius: 8px; }
.ts-btn-secondary:hover { background: #e2e8f0; }
.dark .ts-btn-secondary { background: var(--n-alpha-2, rgba(0,0,0,0.08)); color: var(--n-slate-11); }
.dark .ts-btn-secondary:hover { background: var(--n-alpha-3, rgba(0,0,0,0.12)); }
.ts-btn-ghost { padding: 8px 16px; font-size: 14px; color: var(--n-slate-11); border-radius: 8px; }
.ts-btn-ghost:hover { background: var(--n-alpha-1, rgba(0,0,0,0.04)); }
.dark .ts-btn-ghost { color: var(--n-slate-11); }
.dark .ts-btn-ghost:hover { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }
.ts-btn-ghost-sm { padding: 4px 10px; font-size: 12px; color: var(--n-slate-10); border-radius: 6px; }
.ts-btn-ghost-sm:hover { background: var(--n-alpha-1, rgba(0,0,0,0.04)); }
.ts-btn-emerald { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; font-size: 13px; font-weight: 500; background: #10b981; color: white; border-radius: 8px; }
.ts-btn-emerald:hover { background: #059669; }
.ts-btn-danger { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; font-size: 13px; font-weight: 500; background: #ef4444; color: white; border-radius: 8px; }
.ts-btn-danger:hover { background: #dc2626; }

/* Icon buttons */
.ts-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; color: var(--n-slate-10); transition: all 120ms; }
.ts-icon-btn:hover { background: var(--n-alpha-1, rgba(0,0,0,0.04)); color: var(--n-slate-12); }
.dark .ts-icon-btn:hover { background: var(--n-alpha-2, rgba(0,0,0,0.08)); color: var(--n-slate-11); }
.ts-icon-btn.is-active-emerald { background: #d1fae5; color: #059669; }
.ts-icon-btn-danger:hover { background: #fee2e2; color: #dc2626; }
.dark .ts-icon-btn-danger:hover { background: #7f1d1d; color: #fca5a5; }
.ts-icon-btn-sm { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 4px; color: var(--n-slate-10); }
.ts-icon-btn-sm:hover { background: var(--n-alpha-1, rgba(0,0,0,0.04)); color: var(--n-slate-11); }
.ts-link-btn { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: var(--color-woot, #1f93ff); }
.ts-link-btn:hover { text-decoration: underline; }

/* Form */
.ts-form-row { display: flex; flex-direction: column; gap: 6px; }
.ts-form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ts-form-label { font-size: 13px; font-weight: 500; color: var(--n-slate-11); }
.dark .ts-form-label { color: var(--n-slate-11); }
.ts-form-label-sm { font-size: 12px; color: var(--n-slate-10); }
.ts-form-input, .ts-form-textarea, .ts-form-select { width: 100%; padding: 8px 12px; font-size: 14px; border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); border-radius: 8px; background: var(--color-background); color: var(--n-slate-12); transition: border-color 120ms, box-shadow 120ms; }
.dark .ts-form-input, .dark .ts-form-textarea, .dark .ts-form-select { background: var(--n-alpha-2, rgba(0,0,0,0.08)); color: var(--n-slate-12); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-form-input:focus, .ts-form-textarea:focus, .ts-form-select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
.ts-form-textarea-wrap { position: relative; }
.ts-form-textarea { font-family: inherit; resize: vertical; }
.ts-form-checkbox { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--n-slate-11); cursor: pointer; }
.ts-form-add-row { display: flex; gap: 8px; align-items: center; padding-top: 12px; border-top: 1px solid var(--n-weak, rgba(0,0,0,0.08)); }
.dark .ts-form-add-row { border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-recurrence-config { background: transparent; padding: 12px; border-radius: 8px; border-left: 3px solid #3b82f6; }
.dark .ts-recurrence-config { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }

/* Section title */
.ts-section-title { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--n-slate-11); margin-bottom: 12px; text-align: left; justify-content: flex-start; width: 100%; }
.dark .ts-section-title { color: var(--n-slate-11); }

/* Subtasks */
.ts-subtasks-list { display: flex; flex-direction: column; gap: 4px; }
.ts-subtask-item { display: flex; align-items: center; gap: 10px; padding: 6px 8px; border-radius: 6px; transition: background 120ms; }
.ts-subtask-item:hover { background: transparent; }
.dark .ts-subtask-item:hover { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }

/* Drop zone */
.ts-dropzone { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px 16px; border: 2px dashed #cbd5e1; border-radius: 12px; background: transparent; cursor: pointer; transition: all 150ms; }
.ts-dropzone:hover { border-color: #3b82f6; background: #eff6ff; }
.ts-dropzone.is-active { border-color: var(--color-woot, #1f93ff); background: #dbeafe; }
.dark .ts-dropzone { border-color: var(--n-strong, rgba(0,0,0,0.16)); background: var(--n-alpha-2, rgba(0,0,0,0.08)); }
.dark .ts-dropzone:hover { border-color: #60a5fa; background: #1e3a8a; }

/* Attachments grid */
.ts-attachments-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; margin-top: 16px; }
.ts-attachment-card { display: flex; align-items: center; gap: 10px; padding: 10px; border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); border-radius: 8px; background: var(--color-background); }
.dark .ts-attachment-card { background: var(--n-alpha-2, rgba(0,0,0,0.08)); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-attachment-icon { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 6px; flex-shrink: 0; }
.ts-att-icon-blue { background: #dbeafe; color: var(--color-woot, #1f93ff); }
.ts-att-icon-purple { background: #ede9fe; color: #7c3aed; }
.ts-att-icon-emerald { background: #d1fae5; color: #059669; }
.ts-att-icon-red { background: #fee2e2; color: #dc2626; }
.ts-att-icon-amber { background: #fef3c7; color: #d97706; }
.ts-att-icon-slate { background: var(--n-alpha-1, rgba(0,0,0,0.04)); color: var(--n-slate-10); }
.ts-attachment-info { flex: 1; min-width: 0; }
.ts-attachment-name { font-size: 13px; font-weight: 500; color: var(--color-woot, #1f93ff); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ts-attachment-name:hover { text-decoration: underline; }
.ts-attachment-meta { display: flex; gap: 4px; font-size: 11px; color: var(--n-slate-10); }

/* Time tracker */
.ts-time-display { display: flex; flex-direction: column; align-items: center; padding: 24px; background: linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%); border-radius: 12px; border: 1px solid #d1fae5; }
.dark .ts-time-display { background: linear-gradient(135deg, #064e3b 0%, #164e63 100%); border-color: #065f46; }
.ts-time-display-value { font-size: 36px; font-weight: 700; font-variant-numeric: tabular-nums; font-family: monospace; color: #059669; letter-spacing: 0.05em; }
.dark .ts-time-display-value { color: #6ee7b7; }
.ts-time-display-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--n-slate-10); margin-top: 4px; }
.ts-time-actions { margin-top: 16px; }
.ts-time-entries-list { display: flex; flex-direction: column; gap: 4px; }
.ts-time-entry { display: flex; align-items: center; gap: 8px; padding: 6px 8px; font-size: 12px; border-radius: 6px; background: transparent; }
.dark .ts-time-entry { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }
.ts-time-entry-duration { font-family: monospace; font-weight: 600; width: 64px; color: var(--n-slate-11); }
.dark .ts-time-entry-duration { color: var(--n-slate-11); }
.ts-time-entry-time { color: var(--n-slate-10); }

/* Dependencies */
.ts-deps-list { display: flex; flex-direction: column; gap: 4px; }
.ts-dep-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #fef3c7; border-radius: 6px; font-size: 13px; color: #92400e; }
.dark .ts-dep-item { background: #78350f; color: #fbbf24; }
.ts-search-results { margin-top: 4px; max-height: 200px; overflow-y: auto; border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); border-radius: 8px; background: var(--color-background); box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
.dark .ts-search-results { background: var(--n-alpha-2, rgba(0,0,0,0.08)); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-search-result-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; font-size: 13px; cursor: pointer; transition: background 100ms; }
.ts-search-result-item:hover { background: var(--n-alpha-1, rgba(0,0,0,0.04)); }
.dark .ts-search-result-item:hover { background: var(--n-alpha-3, rgba(0,0,0,0.12)); }

/* Activity */
.ts-comment-composer { position: relative; background: transparent; padding: 12px; border-radius: 8px; margin-bottom: 16px; }
.dark .ts-comment-composer { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }
.ts-comments-list { display: flex; flex-direction: column; gap: 8px; }
.ts-comment-item { display: flex; gap: 10px; padding: 10px; background: transparent; border-radius: 8px; }
.dark .ts-comment-item { background: var(--n-alpha-2, rgba(0,0,0,0.08)); }
.ts-activity-list { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; }
.ts-activity-item { display: flex; align-items: center; gap: 8px; padding: 6px 0; font-size: 12px; border-bottom: 1px dashed #e2e8f0; }
.dark .ts-activity-item { border-color: var(--n-strong, rgba(0,0,0,0.16)); }

/* Avatars */
.ts-avatar-tiny { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 999px; font-size: 10px; font-weight: 700; color: white; flex-shrink: 0; }
.ts-avatar-sm { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 999px; font-size: 12px; font-weight: 700; color: white; flex-shrink: 0; }

/* Badges */
.ts-badge { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 999px; }
.ts-badge-red { background: #fee2e2; color: #b91c1c; }
.ts-badge-blue { background: #dbeafe; color: #1d4ed8; }
.ts-badge-amber { background: #fef3c7; color: #b45309; }
.ts-badge-emerald { background: #d1fae5; color: #047857; }
.ts-badge-slate { background: var(--n-alpha-1, rgba(0,0,0,0.04)); color: var(--n-slate-11); }
.dark .ts-badge-slate { background: var(--n-alpha-3, rgba(0,0,0,0.12)); color: var(--n-slate-11); }

/* Label chip */
.ts-label-chip { font-size: 12px; padding: 3px 10px; border-radius: 6px; border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); color: var(--n-slate-10); transition: all 120ms; }
.ts-label-chip:hover { border-color: var(--n-slate-10); }
.ts-label-chip.is-active { font-weight: 600; }

/* Autocomplete */
.ts-autocomplete { position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px; max-height: 200px; overflow-y: auto; background: var(--color-background); border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); z-index: 50; }
.dark .ts-autocomplete { background: var(--n-alpha-2, rgba(0,0,0,0.08)); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-autocomplete-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; width: 100%; text-align: left; font-size: 13px; cursor: pointer; }
.ts-autocomplete-item:hover, .ts-autocomplete-item.is-active { background: #eff6ff; color: #1d4ed8; }
.dark .ts-autocomplete-item:hover, .dark .ts-autocomplete-item.is-active { background: #1e3a8a; color: #93c5fd; }

/* Card actions always visible */
.ts-card-actions { opacity: 0.6; transition: opacity 120ms; }
.ts-card-actions:hover, .group:hover .ts-card-actions { opacity: 1; }


/* v221 fixes */
.ts-modal-status-pill {
  width: auto !important;
  min-width: 130px;
  max-width: 180px;
  flex-shrink: 0;
}
.ts-modal-title-input {
  flex: 1 1 0;
  min-width: 0;
}
.ts-modal-template-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.ts-modal-template-select {
  width: auto !important;
  max-width: 240px;
  flex-shrink: 0;
}
.ts-modal-header { padding: 16px 20px 10px; }
.ts-modal-pane { padding: 18px 20px; gap: 14px; } 
.ts-modal-pane select, .ts-form-select, .ts-modal-template-select { appearance: auto !important; -webkit-appearance: menulist !important; padding-right: 28px !important; }
.ts-modal-tabs { padding: 0 12px; }
.ts-modal-tab { padding: 9px 12px; font-size: 12px; }
.ts-modal-footer { padding: 12px 20px; }
.ts-form-grid-2 { gap: 12px; }
.ts-form-row { gap: 4px; }
.ts-modal-extra > .ts-modal-pane { border-top: 1px solid var(--n-weak, rgba(0,0,0,0.08)); }
.dark .ts-modal-extra > .ts-modal-pane { border-color: var(--n-strong, rgba(0,0,0,0.16)); }

/* Icon buttons spacing */
.ts-icon-btn { width: 30px; height: 30px; }


/* v222 CSS fixes */
/* Fix arrow position in selects (pad-right + bg-position) */
.ts-modal select,
.ts-modal-status-pill,
.ts-modal-template-select,
.ts-form-select,
.tasks-app select {
  background-position: right 10px center !important;
  padding-right: 36px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
}

/* Status pill: needs more space between text and arrow */
.ts-modal-status-pill {
  padding-left: 12px !important;
  padding-right: 30px !important;
  min-width: 140px !important;
  text-align: left;
}

/* Template select compact but with proper spacing */
.ts-modal-template-select {
  padding-right: 30px !important;
  min-width: 180px;
}

/* View toggle (Lista/Quadro/Agenda) - more breathing room */
.tasks-app header .inline-flex button,
.tasks-app .ml-2.inline-flex button {
  padding: 8px 14px !important;
  font-size: 13px !important;
  gap: 6px !important;
  min-width: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Dark mode container fix: align card bg with parent */
/* Use Chatwoot tokens - no hardcoded slate (which is bluish) */
.tasks-app { background: var(--color-background); }
.tasks-app main { background: transparent; }
.tasks-app ul li.group { transition: background 120ms; }
.tasks-app > header,
.tasks-app > div[class*="border-b"] { background: transparent; }


.ts-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; text-align: center; color: rgb(100, 116, 139); }
.ts-empty-state p { margin: 0; }

/* Board (Kanban) modern layout */
.ts-board-wrap { display: flex; gap: 16px; padding-bottom: 8px; height: calc(100vh - 240px); overflow-x: auto; overflow-y: hidden; }
.ts-board-col { flex: 1 1 0; min-width: 250px; display: flex; flex-direction: column; border-radius: 12px; background: var(--n-alpha-1, rgba(0,0,0,0.04)); border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); overflow: hidden; }
.ts-board-head { padding: 12px 16px; border-top: 3px solid transparent; border-bottom: 1px solid var(--n-weak, rgba(0,0,0,0.08)); display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; background: var(--n-alpha-2, rgba(0,0,0,0.06)); }
.ts-board-body { flex: 1; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.ts-board-card { padding: 10px 12px; border-radius: 8px; border: 1px solid var(--n-weak, rgba(0,0,0,0.08)); background: var(--color-background); cursor: pointer; transition: all 150ms ease; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
.ts-board-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); border-color: var(--n-strong, rgba(0,0,0,0.16)); }
.ts-board-wrap::-webkit-scrollbar { height: 8px; }
.ts-board-wrap::-webkit-scrollbar-thumb { background: var(--n-alpha-3); border-radius: 4px; }
.ts-board-body::-webkit-scrollbar { width: 6px; }
.ts-board-body::-webkit-scrollbar-thumb { background: var(--n-alpha-3); border-radius: 3px; }
</style>
